export const BASE_URL = 'http://localhost:5002/api';
export const LOCAL_BASE_URL = 'http://localhost:5002/api';
export const DASHBOARD_BASE_URL = 'http://44.206.214.121:8080/api/dashboard';
export const EXAM_BASE_URL = 'http://localhost:5002/api/exam';
export const LOCAL_USER_BASE_URL = 'http://localhost:5002/api/users';
export const PAYMENT_BASE_URL = 'http://localhost:5002/api/payment';
export const USER_BASE_URL = 'http://localhost:5002/api/users';
export const PURCHASE_TOGGLE = false;

export const subtopicContent = {
	'9d0e9389-f95e-4dbf-a4cb-d1dd18f31b33': {
		introduction:
			'"Words in Context" is a foundational question type on the Digital SAT Reading and Writing section. It assesses your ability to interpret the meaning of words or phrases based on how they are used in a passage.\n\nRather than asking you to recite dictionary definitions, DSAT questions about words in context test whether you can determine the meaning of a word as it is used in a particular setting. That means you must rely on context clues—the words, phrases, and ideas surrounding the word in question.',
		details:
			'Understanding a word\'s meaning in context is essential for accurate reading comprehension. In real life, we often rely on context to interpret unfamiliar words. The DSAT mimics this by evaluating how well you can do the same in academic texts, fiction, articles, or essays.\n\nKey skills tested:\n• Understanding connotation (positive, negative, or neutral feeling)\n• Recognizing tone and author’s intent\n• Identifying clues in nearby words and sentences\n• Distinguishing among closely related word choices\n\nA typical "Words in Context" question format:\nAs used in line 15, the word “remarkable” most nearly means: A) strange  B) significant  C) rare  D) unexpected\n\nStrategy Guide:\n1. Read the sentence carefully, including surrounding lines.\n2. Paraphrase the sentence, replacing the target word with a blank.\n3. Identify context clues (transition words, tone, definitions, examples, contrasts).\n4. Predict an answer before viewing choices.\n5. Eliminate extreme or tone-mismatched options.\n6. Check for connotation to match the sentence tone.\n\nDenotation vs Connotation:\n• Denotation: literal dictionary definition.\n• Connotation: emotional or cultural meaning (e.g., “childlike” vs “childish”).\n\nCommon context clue types:\n• Definition clue: "Arboreal creatures, or tree‑dwelling animals, include monkeys."\n• Synonym clue: "The plan was audacious and bold."\n• Antonym/contrast clue: "Unlike his gregarious sister, Jonah was reserved."\n• Example clue: "Celestial bodies—like the sun, moon, and stars—light up the night sky."\n• Inference clue: "She winced as the bitter medicine slid down her throat."',
		examples: [
			{
				sentence:
					'The mayor’s tenure was marked by consistent improvement in public transportation and declining crime rates.',
				question: 'As used in the sentence, the word "tenure" most nearly means:',
				options: ['power', 'term', 'success', 'experience'],
				explanation:
					'“Tenure” refers to the period during which someone holds office. The clue is “was marked by...” which refers to a time span.',
				answer: 'term',
			},
			{
				sentence:
					'Though he was usually even-tempered, today Marcus responded to the comment with vehemence.',
				question: 'As used in the sentence, the word "vehemence" most nearly means:',
				options: ['silence', 'emotion', 'confusion', 'strength'],
				explanation:
					'The contrast word "though" tells us this is unusual behavior. So “vehemence” likely refers to strong emotion.',
				answer: 'emotion',
			},
			{
				sentence:
					'The documentary offers a nuanced view of the debate, avoiding extreme perspectives.',
				question: 'As used in the sentence, the word "nuanced" most nearly means:',
				options: ['detailed', 'extreme', 'confusing', 'biased'],
				explanation:
					'The phrase “avoiding extreme perspectives” hints that the view is subtle and layered.',
				answer: 'detailed',
			},
			{
				sentence:
					'Although the painting initially seemed abstract, a closer look revealed a lucid composition.',
				question: 'As used in the sentence, the word "lucid" most nearly means:',
				options: ['clear', 'strange', 'traditional', 'colorful'],
				explanation:
					'The contrast “although” and the phrase “a closer look revealed…” suggests the meaning is clear or understandable.',
				answer: 'clear',
			},
			{
				sentence:
					'She gave a measured response to the criticism, speaking calmly and thoughtfully.',
				question: 'As used in the sentence, the word "measured" most nearly means:',
				options: ['emotional', 'careful', 'short', 'strong'],
				explanation:
					'The context—“speaking calmly and thoughtfully”—suggests a careful and balanced response.',
				answer: 'careful',
			},
		],
		takeaways: [
			'Read the full sentence and surrounding context before selecting.',
			'Paraphrase the sentence to predict the missing word.',
			'Use context clues (transition words, tone, definitions, contrasts).',
			'Eliminate answer choices that are too extreme, vague, or tone-mismatched.',
			'Consider both denotation and connotation to match the author’s intent.',
			'Use the substitution test to confirm your final choice.',
			'Avoid choosing words solely because they sound sophisticated.',
		],
	},
	'dbcfef5f-b0fd-4b65-a588-5fa0a4f2dc7c': {
		introduction:
			"🎯 Introduction: Why This Skill Matters\n\nIn the DSAT Reading and Writing section, you are not just expected to understand what a passage says—you are also asked to figure out how and why the author says it.\n\nThat’s where Text Structure and Purpose come in.\n\nThink of reading as more than just absorbing content—it's like being a detective. You're investigating the author’s motive (purpose) and the method they used to get their message across (structure).\n\nQuestions in this category might ask:\n  • Why did the author include this sentence?\n  • How does this paragraph support the argument?\n  • What is the author trying to do in this passage overall?\n  • What is the structure of the passage?\n\nThese are fundamental skills that help you:\n  • Follow arguments more clearly\n  • Identify main ideas and supporting evidence\n  • Eliminate wrong answer choices confidently\n\nLet’s break these ideas down step by step.",
		details:
			'🧠 What Is Author’s Purpose?\n\n2.1 Definition\nThe author’s purpose is the reason they wrote the passage. It’s the “why” behind the writing.\n\nIt tells us:\n  • What the author wanted the reader to think, feel, or do\n  • What outcome the author hoped to achieve\n\n2.2 Common Types of Purpose on the DSAT\n  – To inform: Provide facts or explanations (explain, describe, show)\n  – To persuade: Convince readers to accept an idea or take action (argue, urge, recommend, support)\n  – To entertain: Amuse or tell a story (narrate, recount, imagine)\n  – To analyze: Break down ideas or issues (examine, evaluate, compare, contrast)\n  – To criticize: Point out problems or flaws (challenge, disprove, question, oppose)\n\n2.3 How to Identify Author’s Purpose\nAsk:\n  • What is the main message the author is trying to send?\n  • Does the author support a particular side?\n  • Is the tone neutral, emotional, critical, or enthusiastic?\n\n2.4 Examples\n  1) “This study reveals that sugary drinks are directly linked to increased risk of diabetes in teenagers. Schools must consider banning soda sales.”\n     🧾 Purpose: To persuade (the author makes an argument and recommends action)\n  2) “The Earth’s rotation causes day and night. As the planet turns, sunlight illuminates different parts of the surface.”\n     🧾 Purpose: To inform (neutral tone, factual explanation)\n\n3. 🔧 What Is Text Structure?\n\n3.1 Definition\nText structure is how the passage is organized to convey the author’s purpose.\n\n3.2 Common Types of Text Structures on the DSAT\n  – Cause and Effect: Shows a reason and its result (because, as a result, due to)\n  – Compare and Contrast: Highlights similarities/differences (similarly, however, unlike)\n  – Problem and Solution: States a problem and suggests a solution (issue, challenge, solution)\n  – Chronological/Sequence: Shows events in order (first, next, finally)\n  – Description/Listing: Lists facts or examples (for example, such as, includes)\n  – Argument and Evidence: Makes a claim supported by data (clearly, for instance, data shows)\n\n3.3 Paragraph Roles (Mini Structures)\n  • Introduction – Main idea or argument\n  • Evidence Paragraphs – Data, examples, or quotes\n  • Counterargument – Presents and refutes opposing views\n  • Conclusion – Summarizes or offers a final thought\n\n4. 📊 How to Approach Structure and Purpose Questions\nStep 1: Skim the question for key phrases (e.g., “What is the author’s purpose…?”, “Which choice best describes the structure…?”)\nStep 2: Summarize the passage in your own words\nStep 3: Identify transitions and clues (tone changes, signal words)\nStep 4: Eliminate options with extreme or unsupported ideas',
		examples: [
			{
				passage:
					'Many scientists have questioned the accuracy of early maps of Mars. These maps, based largely on Earth-based telescopes, were limited by atmospheric distortion. Today, orbiting satellites offer much sharper images and more reliable surface details.',
				question: 'What is the main purpose of the passage?',
				options: [
					'To describe how Mars maps are created',
					'To argue that satellites should replace telescopes',
					'To explain how new technology has improved our knowledge of Mars',
					'To compare Earth and Mars telescopes',
				],
				answer: 'To explain how new technology has improved our knowledge of Mars',
				explanation:
					'The passage highlights how new satellite technology has led to more accurate mapping of Mars compared to earlier methods.',
			},
			{
				passage:
					'Despite major advances in electric vehicles (EVs), battery life and charging infrastructure remain major concerns. In response, some companies are now investing in ultra-fast charging stations.',
				question: 'What role does the second sentence play in the paragraph?',
				options: [
					'It contradicts the first sentence.',
					'It elaborates on a challenge mentioned earlier.',
					'It introduces a potential solution.',
					'It shifts the topic entirely.',
				],
				answer: 'It introduces a potential solution.',
				explanation:
					"The second sentence presents an action taken to address the concern mentioned in the first sentence—it's introducing a solution.",
			},
			{
				passage:
					'The city implemented a new bus schedule to reduce traffic congestion. Initially, the plan showed promise, but within weeks, complaints surged about delays and missed connections.',
				question: 'What structure best describes the passage?',
				options: [
					'Problem–Solution',
					'Chronological Order',
					'Cause and Effect',
					'Claim and Counterclaim',
				],
				answer: 'Claim and Counterclaim',
				explanation:
					'The positive outcome is followed by a negative reaction, creating a claim and counterclaim pattern.',
			},
			{
				passage:
					'This unexpected result forced researchers to rethink their assumptions about photosynthesis in deep-sea organisms.',
				question: 'What is the function of the sentence in its passage?',
				options: [
					'It introduces a theory.',
					'It emphasizes the impact of new evidence.',
					'It explains a scientific process.',
					'It summarizes the passage.',
				],
				answer: 'It emphasizes the impact of new evidence.',
				explanation:
					'The phrase “forced researchers to rethink” highlights how surprising findings changed understanding.',
			},
			{
				passage:
					'Solar panels offer a clean energy source. However, they are expensive to install and depend heavily on weather conditions.',
				question: 'What is the purpose of the word "However"?',
				options: [
					'To provide an example',
					'To contrast two ideas',
					'To summarize the main point',
					'To introduce a timeline',
				],
				answer: 'To contrast two ideas',
				explanation: '"However" signals a shift from a benefit to a limitation.',
			},
			{
				passage:
					'First, the company launched a beta version of its app. Next, it collected user feedback. Finally, the development team implemented major updates before the public release.',
				question: 'What structure does the passage use?',
				options: [
					'Cause and Effect',
					'Compare and Contrast',
					'Chronological Order',
					'Problem–Solution',
				],
				answer: 'Chronological Order',
				explanation:
					'Sequential signal words (“First,” “Next,” “Finally”) establish a time-based structure.',
			},
			{
				passage:
					'The author discusses the role of genetically modified organisms (GMOs) in agriculture, citing both benefits and risks, and ends with a call for more research and regulation.',
				question: 'What is the main purpose of the passage?',
				options: [
					'To warn readers about the dangers of GMOs',
					'To explain how GMOs are produced',
					'To argue for balanced, evidence-based GMO policies',
					'To praise advances in agricultural science',
				],
				answer: 'To argue for balanced, evidence-based GMO policies',
				explanation:
					'The balanced discussion and call for regulation show a persuasive, evidence-based purpose.',
			},
			{
				passage:
					'Many startups fail in their first year. For instance, recent studies show that 90% of tech startups shut down due to poor market fit or lack of funding.',
				question: 'Which structure best fits the passage?',
				options: [
					'Compare and Contrast',
					'Cause and Effect',
					'Sequence of Events',
					'Definition and Example',
				],
				answer: 'Cause and Effect',
				explanation:
					'The passage links startup failures (effect) to specific reasons (causes).',
			},
			{
				passage:
					'These findings challenge the commonly held belief that early humans avoided seafood.',
				question: 'What role does this sentence play in a passage about early human diets?',
				options: [
					'It summarizes the passage’s main idea.',
					'It introduces a new argument.',
					'It highlights a surprising outcome of recent research.',
					'It defines a key term.',
				],
				answer: 'It highlights a surprising outcome of recent research.',
				explanation:
					'The word “challenge” signals an unexpected result, emphasizing new evidence.',
			},
			{
				passage:
					'In the early 1800s, canals revolutionized American trade by connecting inland cities to ports. But by the late 1800s, railroads had largely replaced canals as the dominant mode of transport due to their speed and reliability.',
				question: 'What is the main purpose of this paragraph?',
				options: [
					'To list various transportation methods',
					'To argue that railroads were better than canals',
					'To show the historical transition from canals to railroads',
					'To criticize 19th-century transportation planning',
				],
				answer: 'To show the historical transition from canals to railroads',
				explanation:
					'The paragraph traces a clear shift over time, illustrating a historical transition.',
			},
		],
		takeaways: [
			'Skim the question first to identify whether it asks about purpose, structure, or sentence function.',
			'Summarize the passage mentally to grasp the main message and author’s intent.',
			'Identify author’s purpose by looking for tone and indicator words (e.g., explain, argue, evaluate).',
			'Recognize text structures using signal words (e.g., because, however, first).',
			'Use paragraph roles (introduction, evidence, counterargument, conclusion) to navigate longer texts.',
			'Eliminate options with extreme language, incorrect tones, or unsupported ideas.',
			'Match structure to purpose to avoid trap choices.',
		],
	},
	'2e020b76-3c88-4d26-ae08-0643e56d45f3': {
		introduction:
			"🧭 Introduction: What Are Cross-Text Connections?\n\nThe Digital SAT (DSAT) includes a variety of reading comprehension question types. Among the most advanced and important is the Cross-Text Connection question.\n\nThese questions test your ability to:\n• Read two short, related passages,\n• Understand each author’s viewpoint, tone, and purpose,\n• Compare how the authors' ideas connect, support, oppose, or elaborate upon one another.\n\nIn other words, Cross-Text Connections require you to think like a detective. You must determine how the authors interact with each other’s ideas—whether they agree, disagree, or offer nuanced views on the same subject.",
		details:
			'🎯 Learning Objectives:\n• Identify the central idea and author’s purpose in each passage\n• Recognize relationships between the passages (agreement, disagreement, clarification, etc.)\n• Evaluate evidence and tone to understand how authors develop their arguments\n• Accurately answer cross-text connection questions on the DSAT with confidence\n\n🧠 Why Are Cross-Text Questions Important?\n• They measure higher-level reading and reasoning skills used in college and real life\n• You must analyze, evaluate, and synthesize information across sources\n\n🔍 What Do Cross-Text Questions Look Like?\nDSAT Format:\nYou’ll be shown two short passages (100–150 words each) followed by a question on their relationship.\nSample Prompt:\n"Based on the texts, how would the author of Text 2 most likely respond to the claim in Text 1?"\n\n🔄 Common Types of Relationships:\n• Agreement (supports, confirms, reinforces)\n• Disagreement (opposes, contradicts, disputes)\n• Elaboration (expands, illustrates, extends)\n• Clarification (redefines, clarifies, specifies)\n• Contrast with nuance (partially agrees, qualifies, adds nuance)\n\n✍️ Step-by-Step Approach:\n1. Identify the main idea of each text\n2. Compare viewpoints: agree, disagree, complicate, or extend\n3. Use tone and word clues (e.g., “however,” “similarly,” “although”)\n4. Eliminate wrong choices (extreme language, unsupported ideas)',
		examples: [
			{
				text1: 'Historian Dr. Avery argues that public monuments are vital to preserving cultural memory, acting as educational tools that inspire reflection.',
				text2: 'Writer Carla Yu counters that monuments often reflect political power, not truth, and can distort history by celebrating problematic figures.',
				question: 'How would Yu (Text 2) most likely respond to Avery’s claim in Text 1?',
				options: [
					'She would agree that monuments are useful educational tools.',
					'She would argue that monuments may misrepresent the truth.',
					'She would support Avery’s claim by offering additional examples.',
					'She would propose removing all public monuments entirely.',
				],
				answer: 'She would argue that monuments may misrepresent the truth.',
				explanation:
					'Yu’s tone and content challenge Avery’s claim by highlighting distortions rather than educational value.',
			},
			{
				text1: 'A teacher believes student creativity should drive classroom learning, rather than standardized testing.',
				text2: 'An administrator argues that structure and testing ensure accountability, especially in underperforming schools.',
				question: 'How does Text 2 relate to Text 1?',
				options: ['They agree', 'They disagree', 'They elaborate', 'They ignore the claim'],
				answer: 'They disagree',
				explanation:
					'Text 1 promotes freedom, whereas Text 2 values structure and accountability.',
			},
			{
				text1: 'An environmentalist calls for banning all single-use plastics.',
				text2: 'A chemist suggests that biodegradable alternatives could solve the problem without needing outright bans.',
				question: 'What’s the relationship between the texts?',
				options: ['Agreement', 'Disagreement', 'Elaboration', 'Clarification'],
				answer: 'Elaboration',
				explanation:
					'Text 2 adds nuance by offering an alternative solution rather than rejecting the concern.',
			},
			{
				text1: 'Wildlife corridors are essential for maintaining biodiversity by connecting fragmented habitats.',
				text2: 'Recent studies show that in some cases, corridors lead predators into prey zones, causing population imbalances.',
				question: 'How would the author of Text 2 respond to the claim in Text 1?',
				options: [
					'They strongly support it.',
					'They question its universal effectiveness.',
					'They argue that corridors harm ecosystems in all cases.',
					'They suggest eliminating corridors entirely.',
				],
				answer: 'They question its universal effectiveness.',
				explanation:
					'Text 2 presents a cautionary perspective, emphasizing limitations rather than full opposition.',
			},
			{
				text1: 'Print books promote deeper focus and comprehension than digital formats.',
				text2: 'While digital formats have distractions, they also allow note-taking and dictionary access, which support understanding.',
				question: 'What’s the relationship?',
				options: [
					'Text 2 refutes Text 1.',
					'Text 2 offers a counterexample.',
					'Text 2 adds nuance.',
					'Text 2 repeats Text 1’s argument.',
				],
				answer: 'Text 2 adds nuance.',
				explanation:
					'Text 2 acknowledges drawbacks but highlights additional benefits, complicating the original claim.',
			},
			{
				text1: 'Public murals transform urban spaces and give voice to marginalized communities.',
				text2: 'Some critics argue that murals, without government oversight, can promote controversial or divisive messages.',
				question: 'Which best describes how Text 2 relates to Text 1?',
				options: [
					'It builds on it.',
					'It refines the idea with new support.',
					'It challenges the idea by focusing on risks.',
					'It agrees and offers examples.',
				],
				answer: 'It challenges the idea by focusing on risks.',
				explanation:
					'Text 2 highlights potential negative consequences, countering the unqualified praise in Text 1.',
			},
			{
				text1: 'Funding for space exploration has inspired technological innovations and advanced scientific knowledge.',
				text2: 'Government funds would be better spent on solving Earth’s immediate problems like poverty and health care.',
				question: 'What is the relationship between the texts?',
				options: ['Direct opposition', 'Conditional support', 'Elaboration', 'Unrelated'],
				answer: 'Direct opposition',
				explanation:
					'Text 2 prioritizes humanitarian issues over space exploration, opposing Text 1’s value.',
			},
		],
		takeaways: [
			'Identify each text’s central claim before comparing.',
			'Determine the relationship: agreement, disagreement, elaboration, clarification, or nuanced contrast.',
			'Use tone and signal words to guide your choice.',
			'Follow a step-by-step approach: main idea, compare, tone clues, eliminate.',
			'Underline key claims and jot quick summaries on scrap paper.',
			'Eliminate extreme or unsupported options to avoid traps.',
		],
	},
	'af29bb24-12c3-42db-85dc-7206a2fc38d6': {
		introduction:
			'Introduction: What Are Central Ideas and Details?\n\nIn the Reading and Writing section of the Digital SAT, one common type of question asks you to identify the central idea or interpret supporting details from a short passage. These questions assess your ability to:\n• Understand the main message or purpose of a text.\n• Identify how specific details support that message.\n\nThis is a core skill for college‑level reading: being able to read critically, not just absorb facts, but to understand the bigger picture. Everything you need is in the passage—the challenge is to think clearly, avoid distractions, and focus on what the text is really saying.',
		details:
			'Central Idea vs. Details:\n• Central idea: the main point the passage is trying to convey (the “big picture”).\n• Details: supporting facts, examples, or descriptions that clarify or prove the central idea.\n\nWhy These Questions Matter:\n• Test careful reading and critical thinking.\n• Require separating essential ideas from extra information.\n• Use textual evidence to answer accurately.\n\nQuestion Types:\n1. Main Idea: “Which choice best states the main idea of the text?”\n   – Summarize the passage in your head and match your summary to the best answer.\n2. Detail: “According to the passage, why…?” or “Which detail supports…?”\n   – Find the specific fact, understand its connection, and avoid irrelevant traps.\n\nHow to Approach Main Idea Questions:\n1. Summarize the passage in your own words.\n2. Determine question type (main idea vs. detail).\n3. Predict the answer before looking at choices.\n4. Eliminate choices that add outside info, focus on one detail only, or contradict the passage.\n5. Choose the option that covers most of the passage without adding new content.\n\nHow to Approach Detail Questions:\n1. Read the question carefully and underline key words.\n2. Go back to the passage—reread the relevant sentence(s).\n3. Ask “What does this detail show?” and “How does it connect to the main idea?”\n4. Eliminate answers that overstate, misinterpret tone, or introduce unsupported ideas.\n\nCommon Mistakes:\n• Distracted by interesting but irrelevant details.\n• Picking half‑true answers that don’t capture the whole idea.\n• Adding outside knowledge.\n• Misreading tone (e.g., choosing sarcastic vs. neutral).',
		examples: [
			{
				passage:
					'Dr. Li’s research on ancient tree rings has provided insight into centuries-old climate patterns. By analyzing the width and color of tree rings, Dr. Li can determine years of drought, excessive rainfall, or mild temperatures. Her work has helped scientists better understand how natural climate variations have influenced human history.',
				question: 'Which choice best states the main idea of the passage?',
				options: [
					'Dr. Li studies trees to understand forest growth rates.',
					'Tree rings are colorful indicators of environmental health.',
					'Dr. Li’s research helps uncover how past climates affected human events.',
					'Climate change is caused by natural tree growth.',
				],
				answer: 'Dr. Li’s research helps uncover how past climates affected human events.',
				explanation:
					'The passage focuses on how Dr. Li uses tree‑ring analysis to reveal historical climate impacts on human history.',
			},
			{
				passage:
					'The new library design includes large windows, green rooftop gardens, and solar panels. The architect said the goal was to create an eco-friendly building that also provides a peaceful space for study.',
				question:
					'According to the passage, what is one feature that makes the library eco-friendly?',
				options: [
					'Quiet study areas',
					'Tall bookshelves',
					'Solar panels',
					'Underground parking',
				],
				answer: 'Solar panels',
				explanation:
					'The passage directly mentions solar panels as part of the eco‑friendly design.',
			},
			{
				passage:
					'Professor Alston has developed a new way to teach geometry by integrating music and visual art into her lessons. Her students report being more engaged and retaining information better compared to traditional methods.',
				question: 'Which choice best expresses the main idea of the passage?',
				options: [
					'Professor Alston teaches music and art to math students.',
					'Students prefer traditional teaching methods over creative ones.',
					'Professor Alston’s approach helps students learn geometry more effectively.',
					'Geometry is the hardest math subject to understand.',
				],
				answer: 'Professor Alston’s approach helps students learn geometry more effectively.',
				explanation:
					'The passage highlights how integrating music and art improves student engagement and retention in geometry.',
			},
			{
				passage:
					'Bees play a vital role in pollinating many of the world’s crops. Without bees, foods like almonds, apples, and blueberries would become scarce.',
				question: 'Which crop is mentioned as relying on bee pollination?',
				options: ['Carrots', 'Almonds', 'Rice', 'Wheat'],
				answer: 'Almonds',
				explanation:
					'The passage specifically lists almonds as one of the foods that depend on bee pollination.',
			},
			{
				passage:
					'Despite her young age, Maya has written three novels and started her own publishing company. Her goal is to make the publishing world more accessible to writers from underrepresented backgrounds.',
				question: 'Which choice best states the main idea of the passage?',
				options: [
					'Maya enjoys writing and traveling.',
					'Maya is a young writer working to improve diversity in publishing.',
					'Maya teaches writing to young children.',
					"Maya's books are mostly science fiction.",
				],
				answer: 'Maya is a young writer working to improve diversity in publishing.',
				explanation:
					'The passage emphasizes Maya’s achievements and her mission to support underrepresented writers.',
			},
			{
				passage:
					'During the storm, many roads were flooded, and power lines were damaged. Emergency crews worked overnight to clear the debris and restore electricity.',
				question: 'What was one impact of the storm mentioned in the passage?',
				options: [
					'All schools closed.',
					'Power lines were damaged.',
					'Internet access improved.',
					'The storm caused no harm.',
				],
				answer: 'Power lines were damaged.',
				explanation:
					'The passage clearly states that power lines were damaged by the storm.',
			},
			{
				passage:
					'Over the past decade, scientists have learned more about octopus intelligence. These animals can solve puzzles, open jars, and even escape from enclosures, revealing cognitive abilities that rival those of some mammals.',
				question: 'Which choice best describes the central idea of the text?',
				options: [
					'Octopuses are mammals.',
					'Octopuses can’t be kept in aquariums.',
					'Octopuses show surprising signs of intelligence.',
					'Octopuses are hard to study.',
				],
				answer: 'Octopuses show surprising signs of intelligence.',
				explanation:
					'The passage highlights various behaviors that demonstrate octopus cognitive abilities.',
			},
			{
				passage:
					'In his speech, the mayor emphasized the importance of expanding green spaces in the city. He pointed out that parks improve mental health, reduce pollution, and offer safe areas for children to play.',
				question: 'According to the passage, what is one benefit of green spaces?',
				options: [
					'They increase property taxes.',
					'They reduce pollution.',
					'They attract tourists.',
					'They increase traffic.',
				],
				answer: 'They reduce pollution.',
				explanation:
					'The passage states that parks help reduce pollution as one of their benefits.',
			},
			{
				passage:
					'Historians used to believe that the ancient city of Petra was simply a trading post. Recent discoveries, however, suggest it was also a major cultural center with a complex political system and impressive architecture.',
				question: 'What is the central idea of the passage?',
				options: [
					'Petra was a desert city.',
					'New findings show Petra had a more complex role than previously thought.',
					'Petra was conquered by another empire.',
					'Petra was built only for religious purposes.',
				],
				answer: 'New findings show Petra had a more complex role than previously thought.',
				explanation:
					'The passage contrasts earlier beliefs with new evidence about Petra’s cultural and political significance.',
			},
			{
				passage:
					'To improve food waste management, the school launched a composting program in the cafeteria. Uneaten fruits and vegetables are now collected separately and used to enrich the soil in the school garden.',
				question: 'What does the school do with uneaten fruits and vegetables?',
				options: [
					'Sells them to local restaurants.',
					'Throws them in the trash.',
					'Uses them for compost.',
					'Delivers them to a food bank.',
				],
				answer: 'Uses them for compost.',
				explanation:
					'The passage states that uneaten produce is used in composting to enrich garden soil.',
			},
		],
		takeaways: [
			'Central idea = the big picture; details = supporting facts.',
			'Predict your answer before reviewing choices.',
			'Eliminate options that add outside information or focus on only one detail.',
			'Use keywords to locate relevant sentences for detail questions.',
			'Avoid extreme words unless directly supported by the passage.',
			'Reread passages when unsure—it’s the most reliable source.',
		],
	},
	'18dd10c5-f68d-4a5b-8eb3-54dd943ceee1': {
		introduction:
			'The Digital SAT (DSAT) Reading and Writing section tests your ability to read, understand, and work with written material in a focused, efficient way. One important skill tested is the ability to integrate and connect ideas from different parts of a passage or from multiple notes. This type of question is called a Rhetorical Synthesis question.\nAnother key skill is your understanding of how writers use transitions to guide readers through their ideas, maintain flow, and make their writing clear and coherent. When these two skills are tested together\u2014Rhetorical Synthesis + Transitions\u2014you are asked to connect multiple pieces of information logically and cohesively using the right transition words or phrases.\nIn this chapter, we will cover:\nWhat Rhetorical Synthesis Questions are\nThe role of transitions in writing\nHow transitions are tested within rhetorical synthesis tasks\nCommon types of transitions\nStep-by-step strategies to solve Rhetorical Synthesis + Transitions questions\nExamples with explanations\nCommon mistakes and how to avoid them',
		details:
			'The Digital SAT (DSAT) Reading and Writing section tests your ability to read, understand, and work with written material in a focused, efficient way. One important skill tested is the ability to integrate and connect ideas from different parts of a passage or from multiple notes. This type of question is called a Rhetorical Synthesis question.\nAnother key skill is your understanding of how writers use transitions to guide readers through their ideas, maintain flow, and make their writing clear and coherent. When these two skills are tested together\u2014Rhetorical Synthesis + Transitions\u2014you are asked to connect multiple pieces of information logically and cohesively using the right transition words or phrases.\nIn this chapter, we will cover:\nWhat Rhetorical Synthesis Questions are\nThe role of transitions in writing\nHow transitions are tested within rhetorical synthesis tasks\nCommon types of transitions\nStep-by-step strategies to solve Rhetorical Synthesis + Transitions questions',
		examples: [
			{
				sentence: '',
				question: '',
				options: [],
				answer: '',
				explanation: '',
			},
		],
		takeaways:
			"Rhetorical Synthesis = Combining facts logically to meet a specific purpose\nTransitions = Words that link ideas smoothly and logically\nThese questions test your ability to connect, compare, or contrast facts using the right transition\nFocus first on the goal, then the relevant notes, then the right transition\nMastering this topic will help you not just on the DSAT but also in real-world writing, where clarity, flow, and precision are essential.\n\n\n\nExamples\n\nQuestion 1\nA student is writing about two renewable energy projects.\nNotes:\nProject A uses solar panels to power remote villages.\nProject B converts ocean waves into electricity.\nBoth aim to provide clean energy to underserved regions.\nGoal: Emphasize the common mission of both projects.\nWhich sentence best accomplishes the goal?\nA) Project A powers villages with solar panels, while Project B uses wave energy.\nB) Although the methods differ, both projects aim to supply clean energy to underserved communities.\nC) Project A is about solar energy, but Project B is about the ocean.\nD) Project B uses water, and Project A uses sunlight, creating contrasting energy solutions.\n\u2705 Correct Answer: B\nExplanation:\nChoice B highlights the shared mission and uses the transition \u201calthough\u201d to acknowledge different methods while emphasizing similarity in purpose. Choice A just lists the differences. Choice C and D fail to achieve the synthesis goal.\n\nQuestion 2\nNotes:\nStudy A shows teens benefit from daily meditation.\nStudy B reveals meditation improves academic focus.\nBoth studies emphasize mental health.\nGoal: Highlight how meditation supports teenagers\u2019 mental well-being.\nWhich option is most effective?\nA) Study A shows emotional benefits, whereas Study B proves focus benefits.\nB) Daily meditation helps teens emotionally and academically, supporting their overall well-being.\nC) Meditation can be done at home or in school.\nD) Study A and Study B were both published in psychology journals.\n\u2705 Correct Answer: B\nExplanation:\nChoice B combines the findings and uses synthesis to support the goal. It transitions smoothly with \u201cemotionally and academically,\u201d linking both studies to mental wellness.\n\nQuestion 3\nNotes:\nAuthor 1 claims social media spreads misinformation.\nAuthor 2 argues social media fosters civic engagement.\nBoth discuss social media\u2019s influence.\nGoal: Present a balanced view of social media\u2019s role.\nWhich sentence works best?\nA) Author 1 is critical of social media, but Author 2 praises it.\nB) While Author 1 sees social media as harmful, Author 2 acknowledges its potential to promote civic engagement.\nC) Social media is controversial.\nD) Authors disagree about social media.\n\u2705 Correct Answer: B\nExplanation:\nChoice B uses the transition \u201cwhile\u201d to synthesize contrasting perspectives clearly. It doesn\u2019t just contrast \u2014 it explains the two sides with clarity.\n\nQuestion 4\nNotes:\nOne article supports electric cars for reducing emissions.\nAnother article warns about battery waste and mining impacts.\nBoth mention environmental impact.\nGoal: Show how the articles differ in their view on electric vehicles.\nWhich choice achieves this?\nA) Both articles discuss electric vehicles.\nB) Although both focus on the environment, one promotes electric cars while the other raises environmental concerns.\nC) Electric cars are popular in many countries.\nD) Battery production is controversial.\n\u2705 Correct Answer: B\nExplanation:\nChoice B uses the transition \u201calthough\u201d to set up a contrast, then synthesizes how both discuss environmental impacts in opposite ways.\n\nQuestion 5\nNotes:\nThe 2004 tsunami disaster led to early warning systems.\nA 2020 study showed reduced deaths due to improved alerts.\nGoal: Explain the connection between past disaster and current success.\nWhich choice best meets the goal?\nA) The 2004 tsunami was deadly.\nB) The 2020 study is about fewer deaths.\nC) Because of the 2004 disaster, improved systems now save lives, as seen in the 2020 study.\nD) There were more tsunamis after 2004.\n\u2705 Correct Answer: C\nExplanation:\nChoice C uses \u201cbecause of\u201d to transition from cause to effect, connecting two events into a cohesive argument and showing how one influenced the other.\n\nQuestion 6\nNotes:\nWriter 1 focuses on individual actions in climate change.\nWriter 2 emphasizes government responsibility.\nBoth agree climate change is urgent.\nGoal: Compare the authors\u2019 perspectives while showing shared concern.\nWhich sentence fits best?\nA) Writer 1 and Writer 2 have opposite opinions.\nB) Both writers stress urgency; however, one focuses on individuals and the other on governments.\nC) Writer 2 disagrees with Writer 1.\nD) The topic of climate change is controversial.\n\u2705 Correct Answer: B\nExplanation:\nChoice B uses \u201chowever\u201d to signal contrast, but also begins with \u201cboth writers stress urgency\u201d to synthesize the common point.\n\nQuestion 7\nNotes:\nStudy X promotes a high-protein diet for athletes.\nStudy Y warns against excess protein intake.\nBoth focus on diet\u2019s impact on health.\nGoal: Highlight the tension between the two studies.\nWhich choice works best?\nA) The studies agree on protein.\nB) Protein is important for athletes.\nC) While Study X recommends more protein, Study Y warns of health risks, showing differing views on dietary choices.\nD) Study Y is more recent.\n\u2705 Correct Answer: C\nExplanation:\nChoice C uses a contrast transition (\u201cwhile\u201d) to synthesize differing views on the same topic.\n\nQuestion 8\nNotes:\nOne essay praises Shakespeare\u2019s influence on language.\nAnother essay criticizes the complexity of his writing for modern readers.\nGoal: Present a nuanced view of Shakespeare's impact.\nWhich sentence does this best?\nA) Shakespeare is famous.\nB) His writing is hard to understand.\nC) Although Shakespeare\u2019s language can be complex, his influence on modern English remains significant.\nD) English classes study Shakespeare.\n\u2705 Correct Answer: C\nExplanation:\nChoice C uses \u201calthough\u201d to balance the complexity critique with praise \u2014 this is a clear rhetorical synthesis with a transition built in.\n\nQuestion 9\nNotes:\nResearch A says longer school days improve test scores.\nResearch B shows student burnout increases with longer hours.\nGoal: Emphasize the trade-off shown by both studies.\nWhich option is best?\nA) Research A is about school days.\nB) While longer days boost scores, they also raise burnout risk, as both studies suggest.\nC) School is tiring for many students.\nD) Research B was conducted in 2021.\n\u2705 Correct Answer: B\nExplanation:\nChoice B synthesizes the findings and uses the transition \u201cwhile\u201d to clearly show the trade-off.\n\nQuestion 10\nNotes:\nBook A features a hero who transforms through hardship.\nBook B features a hero who stays the same despite events.\nBoth center on heroic journeys.\nGoal: Contrast the characters\u2019 development styles.\nWhich sentence best accomplishes the goal?\nA) The books are about heroes.\nB) Book A\u2019s hero changes, but Book B\u2019s hero remains the same, revealing different takes on character growth.\nC) One book is longer.\nD) Book B was published later.\n\u2705 Correct Answer: B\nExplanation:\nThis choice uses \u201cbut\u201d to contrast how characters evolve, aligning with the goal and effectively using a transition for synthesis.\n\nThese examples train students to:\nIdentify the goal of the question (similarity, contrast, cause-effect, etc.),\nUse transitions that match the goal (like however, while, because, although),\nEliminate answers that fail to synthesize or miss the goal.",
	},
	'db19699c-cbf5-4e47-83ce-0f8dd739e7fa': {
		introduction:
			'Introduction: What Is “Command of Evidence Based on Textual Inferences”?\n\nThe Digital SAT (DSAT) includes “Command of Evidence” questions that ask not just “What does the passage say?” but “Which line or idea best supports an inference or claim?” These questions require you to connect a conclusion or inference to the precise piece of text that proves it.',
		details:
			'Why These Questions Matter:\n• They test your ability to interpret inferences and back them up with specific proof.\n• They assess critical reading skills—linking claims to exact evidence.\n\nKey Concepts:\n• Inference: a logical conclusion drawn from context clues, not stated outright. (“Despite the cloudy sky, she packed sunscreen” → She expected the sun to return.)\n• Command of Evidence: selecting the sentence, line, or detail that most directly supports a given claim or inference.\n\nTypes of Evidence Questions:\nA. Scientific/Informational: identify data or experimental results that validate a hypothesis.\nB. Literary: match quotes or actions to claims about character, tone, or theme.\n\nStep-By-Step Strategy:\n1. Understand the Claim: restate the inference or conclusion in your own words.\n2. Predict the Evidence: decide what kind of sentence (fact, result, action) would prove it.\n3. Scan for Support: use line numbers or scan the relevant paragraph for that exact idea.\n4. Test Each Option: use a “test phrase” to see which choice directly proves the claim.\n5. Eliminate Weak Lines: discard background, vague, or only loosely related sentences.',
		examples: [
			{
				passage:
					'Researchers studying Arctic foxes observed that individuals living closer to human settlements showed signs of altered behavior, including reduced hunting ranges and increased scavenging activity.',
				question:
					'Which piece of evidence would most directly support the researcher’s claim?',
				options: [
					'Arctic foxes typically live in colder climates with little vegetation.',
					'Foxes closer to humans were found eating from garbage dumps rather than hunting.',
					'Human settlements have been expanding northward in recent decades.',
					'Some foxes migrate seasonally to avoid predators.',
				],
				answer: 'Foxes closer to humans were found eating from garbage dumps rather than hunting.',
				explanation:
					'This line directly links human proximity to increased scavenging behavior, supporting the behavioral shift claim.',
			},
			{
				passage:
					'“As James walked away from the wreckage, his shoulders slumped and his hands trembled. He looked back only once, his eyes wet and lips pressed tight.”',
				question:
					'Which quote best supports the inference that James is emotionally distressed?',
				options: [
					'“He looked back only once.”',
					'“His eyes were wet and lips pressed tight.”',
					'“He walked away from the wreckage.”',
					'“His shoulders slumped and his hands trembled.”',
				],
				answer: '“His shoulders slumped and his hands trembled.”',
				explanation:
					'These physical cues most strongly indicate emotional distress, directly supporting the inference.',
			},
			{
				passage:
					'Although the new policy was expected to increase attendance at town meetings, turnout actually dropped by 15% after its implementation.',
				question: 'Which line best supports the inference that the policy was ineffective?',
				options: [
					'“Turnout dropped by 15% after its implementation.”',
					'“The new policy was expected to increase attendance.”',
					'“Many residents live in remote areas.”',
					'“Town meetings are typically held monthly.”',
				],
				answer: '“Turnout dropped by 15% after its implementation.”',
				explanation:
					'This line provides direct proof that the policy did not achieve its goal, supporting the inference of ineffectiveness.',
			},
			{
				passage:
					'Some historians argue that the fall of the Roman Empire was less a collapse and more a transformation into smaller, regional powers.',
				question: 'Which sentence best supports this argument?',
				options: [
					"Several regional kingdoms preserved Roman laws and language after the empire's fall.",
					'Roman architecture declined significantly after the 5th century.',
					'The Roman army became increasingly dependent on foreign mercenaries.',
					'Taxes during the Roman Empire were especially burdensome.',
				],
				answer: "Several regional kingdoms preserved Roman laws and language after the empire's fall.",
				explanation:
					'Preservation of Roman laws and language demonstrates continuity rather than collapse, directly supporting the transformation argument.',
			},
			{
				passage:
					'In the poem, the narrator views time as a thief, stealing away youth and joy without warning.',
				question: 'Which line from the poem most strongly supports this view?',
				options: [
					'“The days passed slowly, heavy as lead.”',
					'“Youth slipped silently through the cracks of time.”',
					'“A clock ticked steadily in the hallway.”',
					'“Memories shimmered like heat in the distance.”',
				],
				answer: '“Youth slipped silently through the cracks of time.”',
				explanation:
					'This imagery conveys loss of youth, matching the metaphor of time as a thief.',
			},
			{
				passage:
					'Although Sarah told her friends she didn’t care about the competition, she stayed up all night rehearsing her presentation.',
				question: 'What evidence best supports the inference that Sarah actually cared?',
				options: [
					'She asked her teacher how many students would attend.',
					'She chose not to talk about her nerves.',
					'She practiced her speech until 3 a.m.',
					'She had entered many competitions before.',
				],
				answer: 'She practiced her speech until 3 a.m.',
				explanation:
					'Her dedication and effort contradict her words, directly supporting the inference of care.',
			},
			{
				passage:
					'A recent study found that students who slept at least 8 hours before an exam performed better than those who crammed the night before.',
				question: 'Which finding would most directly support the study’s conclusion?',
				options: [
					'Students who got more sleep scored an average of 10% higher.',
					'Students who drank coffee stayed up later.',
					'Many students reported feeling nervous before tests.',
					'Exam difficulty varied by subject.',
				],
				answer: 'Students who got more sleep scored an average of 10% higher.',
				explanation:
					'This statistic directly links more sleep to better performance, backing the study’s claim.',
			},
			{
				passage:
					'“I’m fine,” she said, staring at the untouched food on her plate and twisting her bracelet anxiously.',
				question: 'Which detail best supports the inference that she isn’t actually fine?',
				options: [
					'She didn’t speak for the rest of the meal.',
					'She said she was fine.',
					'She refused to look anyone in the eye.',
					'She showed physical signs of anxiety while denying any problem.',
				],
				answer: 'She showed physical signs of anxiety while denying any problem.',
				explanation:
					'The contrast between her words and body language strongly supports the inference of hidden distress.',
			},
			{
				passage:
					'The author argues that plastic-eating bacteria may provide a solution to global waste accumulation.',
				question: 'Which piece of evidence would most directly support this claim?',
				options: [
					'Some bacteria are found in the digestive systems of animals.',
					'A strain of bacteria was shown to break down PET plastics in lab conditions.',
					'Plastic use has increased dramatically in the past 30 years.',
					'Microplastics are found in ocean fish.',
				],
				answer: 'A strain of bacteria was shown to break down PET plastics in lab conditions.',
				explanation:
					'This finding directly demonstrates the bacteria’s ability to degrade plastic, supporting the solution claim.',
			},
			{
				passage:
					'The narrator uses the phrase “her words fell like pebbles into a deep well” to describe the character’s communication style.',
				question: 'What inference does this image support?',
				options: [
					'Her words were powerful and influential.',
					'Her words were spoken but had no effect.',
					'She was shouting loudly.',
					'She used poetic language.',
				],
				answer: 'Her words were spoken but had no effect.',
				explanation:
					'The simile implies disappearance and ineffectiveness, matching the inference in choice B.',
			},
		],
		takeaways: [
			'Restate the inference or claim clearly before searching for evidence.',
			'Predict the type of text (fact, action, result) that would best support the claim.',
			'Scan the relevant lines for exact matches; avoid loosely related details.',
			'Use the “Because Test” (“This claim is true because…”) to confirm support.',
			'Eliminate options that are background, vague, or only tangentially related.',
			'Trust only what’s in the passage—avoid outside knowledge or assumptions.',
		],
	},
	'c4ae4f4e-d685-47a7-9a35-02c2064ad6b5': {
		introduction:
			'Introduction: What Are Quantitative Inference Questions?\n\nOn the Digital SAT Reading and Writing section, some questions ask you to interpret data presented in graphs, tables, or charts. These are called Command of Evidence based on Quantitative Inferences questions.\n\nThese questions test your ability to:\n• Understand a claim or assertion made in the text.\n• Use a graph or table to support or refute that claim.\n• Identify the best piece of data that supports the statement logically and numerically.\n\nIt’s like being a detective with both a story and a chart, and you have to make sure they match.',
		details:
			'Why Are These Questions Important?\n• Read charts correctly.\n• Find trends in data.\n• Link data to written arguments.\n• Draw correct conclusions based on numbers and patterns.\n\nFormat of Quantitative Evidence Questions:\nYou’ll see a short passage that introduces an idea, a graph/chart/table presenting related data, and a multiple-choice question asking which statement uses the data accurately.\n\nWhat Is Quantitative Inference?\nInference means drawing a conclusion based on evidence—in this case, numerical or graphical data.\n\nWhat Is Command of Evidence?\nSelecting the most accurate data-based sentence that supports or completes a given claim from the passage.\n\nStep-By-Step Strategy:\n1. Skim the Graph/Table: note title, axes, overall trend, key data points.\n2. Read the Paragraph Prompt: identify the claim, subject, and time period.\n3. Evaluate Each Choice Against the Data: check numbers, categories, and relevance.\n4. Select the Best Supporting Data: ensure it is both correct and directly proves the claim.',
		examples: [
			{
				description: 'Line Graph Interpretation',
				passage:
					'The line graph shows a rise in electric car sales from 2015 (100,000 units) to 2022 (1,000,000 units). Passage: “Electric vehicle (EV) adoption is rapidly increasing, suggesting a shift toward more sustainable transportation.”',
				question:
					'Which choice most effectively uses data from the graph to support the passage’s assertion?',
				options: [
					'EV sales stayed consistent from 2015 to 2022.',
					'EV sales fell in 2018 but rebounded in 2019.',
					'EV sales grew tenfold from 2015 to 2022.',
					'Most EV buyers in 2022 were in California.',
				],
				answer: 'EV sales grew tenfold from 2015 to 2022.',
				explanation:
					'Choice C directly uses data (100,000 → 1,000,000 = tenfold increase) to quantitatively support the assertion.',
			},
			{
				description: 'Bar Graph Inference',
				passage:
					'Average daily water usage (liters/person): 2000: 150; 2010: 120; 2020: 100. Passage: “Recent conservation efforts have successfully reduced household water usage.”',
				question: 'Which choice most accurately uses data to support the claim?',
				options: [
					'Water usage increased slightly in 2010.',
					'Water use dropped by 50 liters per person from 2000 to 2020.',
					'2010 had the highest usage rate.',
					'Water conservation is not necessary in urban areas.',
				],
				answer: 'Water use dropped by 50 liters per person from 2000 to 2020.',
				explanation:
					'From 150 → 100 liters = a 50-liter drop, supporting that conservation efforts worked.',
			},
			{
				description: 'Pie Chart and Text Match',
				passage:
					'Energy Sources (2023): Solar: 25%, Wind: 30%, Fossil Fuels: 20%, Hydro: 15%, Other: 10%. Passage: “Renewables now make up the majority of energy sources.”',
				question: 'Which option best supports the claim using data?',
				options: [
					'Wind and solar together make up over half of the total energy sources.',
					'Fossil fuels are the leading source.',
					'Hydro power has overtaken solar energy.',
					'Other sources account for the largest share.',
				],
				answer: 'Wind and solar together make up over half of the total energy sources.',
				explanation:
					'Solar (25%) + Wind (30%) = 55%, supporting that renewables are the majority.',
			},
			{
				description: 'Table Inference',
				passage:
					'Average SAT Scores: Year 2020: Math 510, Reading 495; Year 2021: Math 515, Reading 500; Year 2022: Math 520, Reading 505. Passage: “Students have gradually improved their performance in both sections of the SAT over three years.”',
				question: 'Which choice accurately reflects this data?',
				options: [
					'Math scores dropped from 2020 to 2022.',
					'Reading scores increased by 10 points.',
					'There was no consistent trend in performance.',
					'Math and Reading scores decreased in 2022.',
				],
				answer: 'Reading scores increased by 10 points.',
				explanation:
					'Reading went from 495 → 505 = 10-point increase, directly supporting the claim.',
			},
			{
				description: 'Scatterplot Insight',
				passage:
					'Shows a strong positive correlation between number of study hours and test scores. Passage: “The more time students devote to studying, the better their academic outcomes.”',
				question: 'Which finding would best support this conclusion?',
				options: [
					'Students who studied 10 hours scored 95%, while those who studied 2 hours scored 60%.',
					'Some students prefer to study in the morning.',
					'A few students did not complete the test.',
					'Test difficulty was higher this year.',
				],
				answer: 'Students who studied 10 hours scored 95%, while those who studied 2 hours scored 60%.',
				explanation:
					'Only A provides a quantitative correlation between hours and results, matching the claim.',
			},
			{
				description: 'Mixed Data Set',
				passage:
					'Chart shows city pollution levels (PM2.5) falling from 80 to 40 units over a decade. Passage: “New regulations on emissions have helped cut air pollution in half.”',
				question: 'Which data point best supports this claim?',
				options: [
					'PM2.5 levels dropped from 80 to 40 units between 2010 and 2020.',
					'Vehicle numbers increased during the same time.',
					'Other cities didn’t adopt similar regulations.',
					'The city opened more public parks.',
				],
				answer: 'PM2.5 levels dropped from 80 to 40 units between 2010 and 2020.',
				explanation: 'This proves a 50% decrease, matching the claim.',
			},
			{
				description: 'Time-Series Data Inference',
				passage:
					'Smartphone ownership in teens: 2010: 20%, 2015: 60%, 2020: 95%. Passage: “Smartphones have become nearly universal among teenagers in the past decade.”',
				question: 'Which option best reflects this trend?',
				options: [
					'Ownership rates tripled between 2010 and 2015.',
					'Smartphone use was already universal by 2010.',
					'Teenagers avoid smartphone use today.',
					'Ownership decreased from 2015 to 2020.',
				],
				answer: 'Ownership rates tripled between 2010 and 2015.',
				explanation:
					'20% → 60% = tripling from 2010 to 2015, accurately reflecting the trend.',
			},
			{
				description: 'Venn Diagram Question',
				passage:
					'60 students: 40 like math, 30 like science, 15 like both. Passage: “Most students have an interest in at least one STEM subject.”',
				question: 'Which choice is best supported by the data?',
				options: [
					'Half the students like both subjects.',
					'55 students like at least one of the two subjects.',
					'Only a few students like science.',
					'Interest in STEM is low.',
				],
				answer: '55 students like at least one of the two subjects.',
				explanation: '40 + 30 – 15 = 55, supporting the “most students” claim.',
			},
			{
				description: 'Histogram Analysis',
				passage:
					'Distribution of test scores among 200 students: most scores fall between 70 and 90. Passage: “The majority of students performed well on the exam.”',
				question: 'Which statement best uses the graph to support this?',
				options: [
					'Over 60% of students scored between 70 and 90.',
					'Some students scored below 40.',
					'The test was taken by 200 students.',
					'The average score was exactly 85.',
				],
				answer: 'Over 60% of students scored between 70 and 90.',
				explanation: 'A aligns with the claim about majority performance.',
			},
			{
				description: 'Trend Observation',
				passage:
					'Line graph shows obesity rates increased from 15% to 35% over 30 years. Passage: “Obesity has been steadily rising over the past few decades.”',
				question: 'Which choice best supports this trend?',
				options: [
					'Obesity rose by 20 percentage points in 30 years.',
					'Exercise is important for health.',
					'Sugar intake remained stable.',
					'The graph doesn’t show any change.',
				],
				answer: 'Obesity rose by 20 percentage points in 30 years.',
				explanation:
					'15% → 35% = 20-point increase, directly supporting the “steadily rising” claim.',
			},
		],
		takeaways: [
			'Skim the graph or table first for context before reading the question.',
			'Identify the specific claim in the passage and the relevant data range.',
			'Compare each answer choice carefully against the data: numbers, categories, time periods.',
			'Eliminate choices with incorrect data or that are true but irrelevant.',
			'Choose the sentence that is both factually correct and directly supports the claim.',
			'Trust the chart’s labels and axes—avoid making assumptions beyond what’s shown.',
		],
	},
	'9b4e0195-c4f3-42df-ae18-9c9f7f537db7': {
		introduction:
			'Transitions and Clues\nPay attention to:\nTone changes\nTransition words (however, furthermore, in contrast)\nRepeated ideas or shifts in topic\nStep 4: Use Process of Elimination\nRemove options with:\nExtreme language (\u201calways,\u201d \u201cnever\u201d)\nIncorrect tones (sarcastic vs. serious)\nInformation not found in the passage\n\n5. \ud83e\uddea Sample Mini-Passage + Analysis\nMini-Passage:\n\u201cScientists originally believed that Pluto was the ninth planet. However, later discoveries revealed that it shared more in common with icy bodies in the Kuiper Belt, prompting astronomers to reclassify it as a dwarf planet.\u201d\n\ud83d\udccd Author\u2019s Purpose:\nTo inform about a reclassification in astronomy\n\ud83d\udccd Text Structure:\nCause and effect \u2013 the new discoveries caused Pluto to be reclassified\n\n6. \ud83d\udd01 How Structure Supports Purpose\nThink of structure as the vehicle that carries the message (purpose).\nTip: Matching the structure to the goal helps eliminate wrong answers and pick the most logical one.\n\n7. \ud83d\udca1 Common Question Types and Traps\n7.1 \u201cWhy Did the Author Include This?\u201d\nTrap: Picking a choice that sounds true but isn\u2019t the reason for inclusion.\nStrategy: Ask what new function that sentence serves. Does it introduce, support, contrast, or conclude?\n\n7.2 \u201cWhat Is the Main Purpose?\u201d\nTrap: Picking an answer that focuses on details, not the big idea.\nStrategy: Use your summary of the whole text. Stay general, not specific.\n\n7.3 \u201cWhat Is the Structure?\u201d\nTrap: Confusing time sequence with cause-effect or comparison with contrast.\nStrategy: Pay attention to transitions and shifts between ideas.\n\n8. \u2705 Practice Exercise\nPassage:\n\u201cNo matter how we irrigate the sand or tame the forest, the sea remains beyond our control. We may shape land, but we cannot command the tides.\u201d\nQuestion: What is the author\u2019s purpose?\nA. To criticize environmental policies\nB. To argue that nature is superior to human strength\nC. To describe efforts to preserve nature\nD. To explain how tides are formed\n\u2705 Correct Answer: B\nThe author contrasts what humans can do with what they can\u2019t, emphasizing nature\u2019s power.\n\n9. \u270d\ufe0f Tips for Mastery\n\ud83d\udcd8 Active Reading\nDon\u2019t skim mindlessly. Read with questions in mind:\n\u201cWhat is the author trying to say?\u201d\n\u201cHow is this part connected to the one before it?\u201d\n\ud83e\udde9 Break It Down\nBefore answering, break the passage into parts:\nMain idea\nSupporting points\nTransitions\n\ud83d\udcac Paraphrase Mentally\nPut the argument in your own words.\nThis helps eliminate choices that sound smart but don\u2019t fit.\n\n10. \ud83d\udcdd Summary Chart: Purpose vs. Structure\n\n11. \ud83c\udf1f Final Review: 5-Point Checklist\n\u2705 Can I summarize the passage in 1\u20132 sentences?\n\u2705 Did I identify any transitions between ideas?\n\u2705 Can I tell what the author wants me to think or learn?\n\u2705 Did I notice any patterns in the structure (cause-effect, comparison)?\n\u2705 Can I eliminate any answer that adds ideas the author never mentioned?\n\n1',
		details:
			'Transitions and Clues\nPay attention to:\nTone changes\nTransition words (however, furthermore, in contrast)\nRepeated ideas or shifts in topic\nStep 4: Use Process of Elimination\nRemove options with:\nExtreme language (\u201calways,\u201d \u201cnever\u201d)\nIncorrect tones (sarcastic vs. serious)\nInformation not found in the passage\n\n5. \ud83e\uddea Sample Mini-Passage + Analysis\nMini-Passage:\n\u201cScientists originally believed that Pluto was the ninth planet. However, later discoveries revealed that it shared more in common with icy bodies in the Kuiper Belt, prompting astronomers to reclassify it as a dwarf planet.\u201d\n\ud83d\udccd Author\u2019s Purpose:\nTo inform about a reclassification in astronomy\n\ud83d\udccd Text Structure:\nCause and effect \u2013 the new discoveries caused Pluto to be reclassified\n\n6. \ud83d\udd01 How Structure Supports Purpose\nThink of structure as the vehicle that carries the message (purpose).\nTip: Matching the structure to the goal helps eliminate wrong answers and pick the most logical one.\n\n7. \ud83d\udca1 Common Question Types and Traps\n7.1 \u201cWhy Did the Author Include This?\u201d\nTrap: Picking a choice that sounds true but isn\u2019t the reason for inclusion.\nStrategy: Ask what new function that sentence serves. Does it introduce, support, contrast, or conclude?\n\n7.2 \u201cWhat Is the Main Purpose?\u201d\nTrap: Picking an answer that focuses on details, not the big idea.\nStrategy: Use your summary of the whole text. Stay general, not specific.\n\n7.3 \u201cWhat Is the Structure?\u201d\nTrap: Confusing time sequence with cause-effect or comparison with contrast.\nStrategy: Pay attention to transitions and shifts between ideas.\n\n8. \u2705 Practice Exercise\nPassage:\n\u201cNo matter how we irrigate the sand or tame the forest, the sea remains beyond our control. We may shape land, but we cannot command the tides.\u201d\nQuestion: What is the author\u2019s purpose?\nA. To criticize environmental policies\nB. To argue that nature is superior to human strength\nC. To describe efforts to preserve nature\nD. To explain how tides are formed\n\u2705 Correct Answer: B\nThe author contrasts what humans can do with what they can\u2019t, emphasizing nature\u2019s power.\n\n9. \u270d\ufe0f Tips for Mastery\n\ud83d\udcd8 Active Reading\nDon\u2019t skim mindlessly. Read with questions in mind:\n\u201cWhat is the author trying to say?\u201d\n\u201cHow is this part connected to the one before it?\u201d\n\ud83e\udde9 Break It Down\nBefore answering, break the passage into parts:\nMain idea\nSupporting points\nTransitions\n\ud83d\udcac Paraphrase Mentally\nPut the argument in your own words.\nThis helps eliminate choices that sound smart but don\u2019t fit.\n\n10. \ud83d\udcdd Summary Chart: Purpose vs. Structure\n\n11. \ud83c\udf1f Final Review: 5-Point Checklist\n\u2705 Can I summarize the passage in 1\u20132 sentences?\n\u2705 Did I identify any transitions between ideas?\n\u2705 Can I tell what the author wants me to think or learn?\n\u2705 Did I notice any patterns in the structure (cause-effect, comparison)?\n\u2705 Can I eliminate any answer that adds ideas the author never mentioned?\n\n12. \ud83c\udfc1 Final Thoughts\nMastering text structure and purpose is about developing reading confidence. It\u2019s about knowing that every sentence, every paragraph has a reason\u2014and understanding what that reason is.\nBy training yourself to:\nSpot the author\u2019s motive\nDetect how the argument unfolds\nUnderstand the role each part plays\n\u2014you\u2019ll not only master DSAT reading questions, but also become a stronger, more analytical reader in life.',
		examples: [
			{
				sentence: '',
				question: '',
				options: [
					'A) To describe how Mars maps are created',
					'B) To argue that satellites should replace telescopes',
					'C) To explain how new technology has improved our knowledge of Mars',
					'D) To compare Earth and Mars telescopes',
				],
				answer: '',
				explanation: '',
			},
			{
				sentence: '',
				question: '',
				options: [
					'A) It contradicts the first sentence.',
					'B) It elaborates on a challenge mentioned earlier.',
					'C) It introduces a potential solution.',
					'D) It shifts the topic entirely.',
				],
				answer: 'C',
				explanation: '',
			},
			{
				sentence: '',
				question: '',
				options: [
					'A) Problem\u2013Solution',
					'B) Chronological order',
					'C) Cause and Effect',
					'D) Claim and Counterclaim',
				],
				answer: 'C',
				explanation: '',
			},
			{
				sentence: '',
				question: '',
				options: [
					'A) It introduces a theory.',
					'B) It emphasizes the impact of new evidence.',
					'C) It explains a scientific process.',
					'D) It summarizes the passage.',
				],
				answer: 'D',
				explanation: '',
			},
			{
				sentence: '',
				question: '',
				options: [
					'A) To provide an example',
					'B) To contrast two ideas',
					'C) To summarize the main point',
					'D) To introduce a timeline',
				],
				answer: 'B',
				explanation: '',
			},
			{
				sentence: '',
				question: '',
				options: [
					'A) Cause and Effect',
					'B) Compare and Contrast',
					'C) Chronological Order',
					'D) Problem\u2013Solution',
				],
				answer: 'B',
				explanation: '',
			},
			{
				sentence: '',
				question: '',
				options: [
					'A) To warn readers about the dangers of GMOs',
					'B) To explain how GMOs are produced',
					'C) To argue for balanced, evidence-based GMO policies',
					'D) To praise advances in agricultural science',
				],
				answer: 'C',
				explanation: '',
			},
			{
				sentence: '',
				question: '',
				options: [
					'A) Compare and Contrast',
					'B) Cause and Effect',
					'C) Sequence of Events',
					'D) Definition and Example',
				],
				answer: 'C',
				explanation: '',
			},
			{
				sentence: '',
				question: '',
				options: [
					'A) It summarizes the passage\u2019s main idea.',
					'B) It introduces a new argument.',
					'C) It highlights a surprising outcome of recent research.',
					'D) It defines a key term.',
				],
				answer: 'B',
				explanation: '',
			},
			{
				sentence: '',
				question: '',
				options: [
					'A) To list various transportation methods',
					'B) To argue that railroads were better than canals',
					'C) To show the historical transition from canals to railroads',
					'D) To criticize 19th-century transportation planning',
				],
				answer: 'C',
				explanation: '',
			},
			{
				sentence: '',
				question: '',
				options: [],
				answer: 'C',
				explanation: '',
			},
		],
		takeaways: 'Table of Types',
	},
	'fb01ef96-10a1-4a55-b3e8-b882cfb83f99': {
		introduction:
			'In the DSAT Reading & Writing module, “Boundaries” questions assess your understanding of sentence structure and correct punctuation usage—how to separate or connect ideas, clauses, or phrases. Think of punctuation as road signs: missing or misleading marks make sentences as confusing as a driver without directions.',
		details:
			'Learning Objectives:\n• Understand what “Boundaries” questions test.\n• Identify independent vs. dependent clauses.\n• Apply period, comma, semicolon, colon, and dash rules to join or separate ideas.\n• Recognize and correct comma splices, run-on sentences, sentence fragments, and dangling modifiers.\n• Use a systematic strategy to eliminate wrong choices and select the best punctuation.\n\nKey Concepts:\n1. Clause Types:\n  – Independent clause: complete thought (e.g., “She smiled.”)\n  – Dependent clause: incomplete, needs attachment (e.g., “Because she smiled”).\n\n2. Punctuation Rules:\n  • Period (.): separate two independent clauses.\n  • Comma (,):\n    – + conjunction (and/but/or/so) to join clauses.\n    – After introductory elements.\n    – To set off nonessential information.\n  • Semicolon (;):\n    – Connect two related independent clauses without conjunction.\n    – Separate complex list items.\n  • Colon (:):\n    – After a complete sentence to introduce a list, explanation, or result.\n  • Dash (—):\n    – Add emphasis or dramatic detail.\n    – Set off nonessential info.\n\n3. Common Errors to Avoid:\n  – Comma splice: two full sentences joined by a comma.\n  – Run-on: independent clauses with no punctuation.\n  – Fragment: missing subject or verb.\n  – Dangling modifier: opening phrase doesn’t match the subject that follows.\n\n4. Strategy:\n  1) Read the whole sentence.\n  2) Identify clause types around the blank.\n  3) Eliminate punctuation that causes splices, fragments, or misplacement.\n  4) Read aloud to confirm clarity and flow.',
		examples: [
			{
				question:
					'The train was late the passengers were frustrated. Which revision is correct?',
				options: [
					'The train was late the passengers, were frustrated.',
					'The train was late; the passengers were frustrated.',
					'The train was late, the passengers were frustrated.',
					'The train was late: the passengers were frustrated.',
				],
				answer: 'The train was late; the passengers were frustrated.',
				explanation:
					'Use a semicolon to join two related independent clauses without a conjunction.',
			},
			{
				question: 'After reading the final chapter of the novel. She closed the book.',
				options: [
					'After reading the final chapter of the novel. She closed the book.',
					'After reading the final chapter of the novel she closed the book.',
					'After reading the final chapter of the novel, she closed the book.',
					'She closed the book, after reading the final chapter of the novel.',
				],
				answer: 'After reading the final chapter of the novel, she closed the book.',
				explanation: 'An introductory dependent clause must be followed by a comma.',
			},
			{
				question:
					'Tim the class president delivered a powerful speech. Which version uses punctuation correctly?',
				options: [
					'Tim the class president delivered a powerful speech.',
					'Tim the class president, delivered a powerful speech.',
					'Tim, the class president, delivered a powerful speech.',
					'Tim—the class president delivered a powerful speech.',
				],
				answer: 'Tim, the class president, delivered a powerful speech.',
				explanation:
					'Set off the nonessential appositive “the class president” with commas.',
			},
			{
				question:
					'The play was beautifully written, the performances were outstanding. Which choice best corrects the sentence?',
				options: [
					'written, the performances',
					'written. The performances',
					'written: the performances',
					'written and the performances',
				],
				answer: 'written. The performances',
				explanation:
					'This comma splice is fixed by using a period to separate two independent clauses.',
			},
			{
				question:
					'She loved art history she spent hours at the museum. Which choice best maintains the sentence structure?',
				options: [
					'art history, she',
					'art history; she',
					'art history: she',
					'art history she',
				],
				answer: 'art history; she',
				explanation:
					'Use a semicolon to link two complete thoughts that are closely related.',
			},
			{
				question:
					'He bought all the essentials camping gear, food, and a first-aid kit. Which version is correct?',
				options: [
					'essentials: camping gear, food, and a first-aid kit',
					'essentials; camping gear, food, and a first-aid kit',
					'essentials, camping gear, food, and a first-aid kit',
					'essentials camping gear, food, and a first-aid kit',
				],
				answer: 'essentials: camping gear, food, and a first-aid kit',
				explanation: 'A colon introduces a list only after a complete sentence.',
			},
			{
				question:
					'While the students waited for the bus. Which revision makes this a complete sentence?',
				options: [
					'The students waited.',
					'While waiting, the students.',
					'The students waited for the bus.',
					'While the students waited for the bus, the rain began to fall.',
				],
				answer: 'While the students waited for the bus, the rain began to fall.',
				explanation:
					'Add an independent clause to complete the dependent introductory phrase.',
			},
			{
				question:
					'Tired after the long hike, the backpack was heavy. Which revision best corrects the modifier?',
				options: [
					'Tired after the long hike, it was heavy.',
					'Tired after the long hike, the group collapsed.',
					'Tired after the long hike, the backpack fell.',
					'Tired, the hike made us exhausted.',
				],
				answer: 'Tired after the long hike, the group collapsed.',
				explanation:
					'The dangling modifier must modify a person performing the action, not an object.',
			},
			{
				question:
					'The team practiced every day, and improved significantly. Which version is correct?',
				options: [
					'every day and improved',
					'every day, and improved',
					'every day, and improved significantly',
					'every day and improved significantly',
				],
				answer: 'every day, and improved significantly',
				explanation:
					'Join two independent clauses with a comma plus the coordinating conjunction “and.”',
			},
			{
				question:
					'The sound of distant thunder, signaled the arrival of the storm. Which choice best fixes the error?',
				options: [
					'thunder signaled',
					'thunder; signaled',
					'thunder, and signaled',
					'thunder. Signaled',
				],
				answer: 'thunder signaled',
				explanation: 'Never separate a subject and verb with a comma.',
			},
		],
		takeaways: [
			'Know the difference between independent and dependent clauses before choosing punctuation.',
			'Use periods, commas (+ conjunction), semicolons, colons, and dashes according to their specific boundary functions.',
			'Avoid comma splices, run-on sentences, fragments, and dangling modifiers by matching punctuation to clause types.',
			'Follow a clear strategy: read the full sentence, identify clause boundaries, eliminate incorrect punctuation, and check clarity aloud.',
			'Never insert punctuation between a subject and its verb or a preposition and its object.',
			'Practice identifying essential vs. nonessential information to know when commas or dashes are required.',
		],
	},
	'8ab383be-f144-419b-8cf1-2be93c229d47': {
		introduction:
			'On the DSAT Reading & Writing section, Form, Structure, and Sense questions ask you to revise and edit sentences for grammatical correctness and logical consistency. You’ll choose the option that best applies Standard English rules—grammar, sentence structure, verb forms, modifier placement, pronoun-antecedent and subject-verb agreement, and overall clarity of meaning.',
		details:
			'Key Grammar Rules & Concepts:\n• Subject-Verb Agreement: singular subjects take singular verbs; watch out for intervening phrases.\n• Verb Tense Consistency: maintain the same tense unless context demands a change.\n• Pronoun-Antecedent Agreement: pronouns must match their nouns in number and clarity.\n• Modifier Placement: place descriptive phrases next to the noun they modify to avoid dangling or misplaced modifiers.\n• Parallel Structure: list items or actions in the same grammatical form.\n• Plural vs. Possessive Nouns: distinguish plurals (cats) from singular (cat’s) and plural possessives (cats’).\n\nSentence Construction:\n• Complete Sentences vs. Fragments: ensure each sentence has a subject and verb.\n• Run-on Sentences: use proper punctuation (semicolon, period, or comma + conjunction) to join independent clauses.\n\nStrategy:\n1. Read the entire sentence and identify the grammar issue being tested (verb form, modifier, pronoun, etc.).\n2. Plug each answer choice into the sentence and check for agreement, placement, and logical flow.\n3. Eliminate options that create fragments, splices, misplaced modifiers, or ambiguity.\n4. Choose the version that reads naturally and follows Standard English conventions.',
		examples: [
			{
				question: 'Each of the puppies in the litter ____ to be adopted by next week.',
				options: ['hope', 'hoping', 'hopes', 'have hoped'],
				answer: 'hopes',
				explanation:
					'“Each” is singular, so the verb must be singular: hopes. The prepositional phrase “of the puppies” does not affect agreement.',
			},
			{
				question: 'Excited by the news, ____ ran to tell her friends.',
				options: [
					'the phone rang and Maya',
					'Maya, the phone ringing,',
					'Maya',
					'ringing loudly, Maya',
				],
				answer: 'Maya',
				explanation:
					'The introductory modifier “Excited by the news” must describe Maya, the noun immediately following the comma.',
			},
			{
				question: 'When I was ten years old, I ____ to be an astronaut.',
				options: ['dream', 'have dreamed', 'dreamed', 'am dreaming'],
				answer: 'dreamed',
				explanation:
					'The main clause is in past tense (“was”), so the verb must also be past tense: dreamed.',
			},
			{
				question: 'Neither of the boys remembered to bring ____ backpack.',
				options: ['their', 'his', 'her', 'its'],
				answer: 'his',
				explanation: '“Neither” is singular, so the pronoun must be singular: his.',
			},
			{
				question: 'The ____ paws left muddy prints all over the floor.',
				options: ['dog’s', 'dogs', 'dogs’', 'dog'],
				answer: 'dogs',
				explanation:
					'We need the plural noun “dogs” (no possession). There is no apostrophe since it’s not possessive.',
			},
			{
				question: 'To survive harsh winters, ____ thick fur keeps them warm.',
				options: ['wolves’', 'wolves', 'wolf’s', 'wolf'],
				answer: 'wolves’',
				explanation:
					'The modifier “To survive harsh winters” applies to the wolves, and we need plural possessive: wolves’.',
			},
			{
				question: 'After the long hike through the mountains.',
				options: [
					'She slept peacefully through the night.',
					'The campers built a fire.',
					'After the long hike through the mountains, we stopped to rest.',
					'Hiking for miles and miles.',
				],
				answer: 'After the long hike through the mountains, we stopped to rest.',
				explanation:
					'This completes the fragment by adding an independent clause to the introductory dependent phrase.',
			},
			{
				question: 'Walking through the museum, the painting ____ caught our attention.',
				options: ['hanging on the wall', 'was', 'we saw', 'hanging on the wall was'],
				answer: 'hanging on the wall was',
				explanation:
					'The modifier “Walking through the museum” must relate to us, but the structure requires the subject “the painting” immediately after, so “the painting hanging on the wall was...”',
			},
			{
				question:
					'When Emily gave Lucy her lunch, she smiled. What’s wrong with this sentence?',
				options: [
					'“her” should be changed to “hers”',
					'It’s unclear who “she” refers to',
					'“gave” should be changed to “gives”',
					'No change is needed',
				],
				answer: 'It’s unclear who “she” refers to',
				explanation:
					'The pronoun “she” is ambiguous—you don’t know if it refers to Emily or Lucy. The sentence must be rewritten for clarity.',
			},
			{
				question: 'Jake enjoys swimming, to run, and biking.',
				options: [
					'swimming, running, and biking',
					'to swim, run, and bike',
					'swimming, run, and biking',
					'swimming, running, and to bike',
				],
				answer: 'swimming, running, and biking',
				explanation:
					'For parallel structure, all items must follow the same grammatical form (gerunds).',
			},
		],
		takeaways: [
			'Ensure subject and verb agree in number, ignoring intervening phrases.',
			'Maintain consistent verb tense unless context demands a shift.',
			'Match pronouns clearly and unambiguously to their antecedents.',
			'Place modifiers immediately before or after the noun they describe.',
			'Use parallel construction for lists of actions or items.',
			'Differentiate plural nouns from possessive forms with apostrophes.',
			'Complete sentences must have both subject and verb—avoid fragments and run-ons.',
			'Read each answer choice in context, eliminate those that disrupt grammar or logic, and choose the most natural-sounding revision.',
		],
	},
	'ce519871-2bd1-4018-97f9-60ae89e2c5b1': {
		introduction:
			'In algebra, we often encounter expressions and equations that look different but behave the same way. These are called equivalent expressions and equivalent equations. Mastering this concept helps in simplifying problems, solving equations, and understanding mathematical relationships in a deeper way. This chapter will guide you through the principles, properties, and problem-solving techniques necessary to identify, create, and manipulate equivalent expressions and equations effectively.',
		details:
			'➤ What Are Expressions?\nAn expression is a mathematical phrase that can include numbers, variables (letters), and operation signs (+, –, ×, ÷), but no equals sign.\nExamples:\n• 3x + 2\n• x² − 5x + 6\n• 2(a + b)\nExpressions can be simplified or rewritten using mathematical properties. If two expressions always have the same value for the same input, they are called equivalent expressions.\n\n➤ Equivalent Expressions\nTwo expressions are said to be equivalent if they produce the same result for all values of the variable(s).\nExample:\n• 2(x + 3) and 2x + 6 are equivalent since 2(x + 3) = 2x + 6 by distributive property.\n\n➤ Key Properties:\na. Commutative Property: a + b = b + a; ab = ba\nb. Associative Property: (a + b) + c = a + (b + c)\nc. Distributive Property: a(b + c) = ab + ac\nd. Combining Like Terms: 3x + 5x = 8x\n\n➤ Simplifying Expressions\nSteps:\n1. Apply distributive property\n2. Combine like terms\n3. Apply exponent rules if needed\nExample:\nSimplify 2x + 3(x + 4)\n→ Apply distributive: 2x + 3x + 12\n→ Combine terms: 5x + 12\n\n➤ What Are Equations?\nAn equation shows equality between two expressions using an equal sign (=).\nExamples:\n• x + 2 = 7\n• 3x − 5 = 10\nSolving means finding the variable’s value that makes the equation true.\n\n➤ Equivalent Equations\nTwo equations are equivalent if they have the same solution set.\nExample:\n• x + 3 = 7 and x = 4 are equivalent since both are true when x = 4.\n\n➤ Making Equivalent Equations\nApply the same operation on both sides:\nExample:\nx − 5 = 10 → Add 5 → x = 15\n\n➤ Solving via Equivalent Forms\nSteps:\n1. Distribute\n2. Combine like terms\n3. Move variables to one side\n4. Move constants to other side\n5. Isolate the variable\n\n➤ Substitution Check\nTo verify equivalence:\n1. Choose a value for x\n2. Evaluate both sides\nExample:\nAre 3(x + 2) and 3x + 6 equivalent?\nTry x = 1 and x = 4 → both give the same result (9 and 18)\n\n➤ Real-Life Applications\na. Shopping: 5x + 3x = 8x (combining costs)\nb. Distance Problems: 60t = 50(t + 1) → simplify to solve\n\n➤ Common Mistakes:\n• (a + b)² ≠ a² + b² → correct: a² + 2ab + b²\n• (a + b)/a ≠ 1 + b → correct: 1 + (b/a)\n• Assuming x + 2 = x + 5 ⇒ 2 = 5 (invalid simplification)',
		examples: [
			{
				passage: 'Are 4(x + 2) and 4x + 8 equivalent?',
				question:
					'Apply the distributive property to check if the two expressions are equivalent.',
				options: ['Yes', 'No', 'Only when x = 2', 'Only when x = 0'],
				answer: 'Yes',
				explanation: 'Distribute 4: 4(x + 2) = 4x + 8. So the expressions are equivalent.',
			},
			{
				passage: 'Solve: 3x + 2 = 5x − 4',
				question: 'What is the value of x?',
				options: ['x = 6', 'x = 3', 'x = -3', 'x = -6'],
				answer: 'x = 3',
				explanation: '3x + 2 = 5x − 4 → −2x = −6 → x = 3',
			},
			{
				passage: 'Is x(x + 1) equivalent to x² + 1?',
				question: 'Use distribution to determine equivalence.',
				options: ['Yes', 'No', 'Only when x = 0', 'Only when x = 1'],
				answer: 'No',
				explanation: 'x(x + 1) = x² + x, not x² + 1.',
			},
			{
				passage: 'Simplify: 2x + 3(x − 2) − x',
				question: 'What is the simplified expression?',
				options: ['4x − 6', '2x + 3x − 2 − x', '5x − 6', '3x − 6'],
				answer: '4x − 6',
				explanation: 'Distribute: 3(x − 2) = 3x − 6 → 2x + 3x − 6 − x = 4x − 6',
			},
			{
				passage: 'Solve: 4(x − 1) = 2(x + 3)',
				question: 'What is the value of x?',
				options: ['x = 5', 'x = 3', 'x = 4', 'x = 6'],
				answer: 'x = 5',
				explanation: '4x − 4 = 2x + 6 → 2x = 10 → x = 5',
			},
		],
		takeaways: [
			'Equivalent expressions have the same value for all variable inputs.',
			'Use algebraic properties like distributive, commutative, and associative to simplify expressions.',
			'Two equations are equivalent if they have the same solutions.',
			'Always perform the same operation on both sides of an equation to maintain balance.',
			'Check for equivalence using substitution and simplification.',
			'Avoid common algebraic mistakes—especially with exponent rules and distribution.',
		],
	},
	'7c9ea32c-426a-4643-a3e4-f297f2302f21': {
		introduction:
			'In mathematics, exponential functions play a vital role in describing real-world scenarios involving growth and decay. From population growth and radioactive decay to interest calculations and viral spread, exponential models allow us to understand how quantities evolve over time.\n\nAn exponential function is one in which the variable is in the exponent, rather than the base. This fundamental structure causes exponential functions to behave very differently from linear or quadratic functions.\n\nGeneral Form:\nf(x) = ab^x\n• a: initial value or coefficient\n• b: base of the exponential (must be positive and not equal to 1)\n• x: exponent (independent variable)',
		details:
			'➤ Basic Properties of Exponential Functions:\n• Domain: (−∞, ∞)\n• Range: (0, ∞) if a > 0\n• Y-intercept: f(0) = a\n• Horizontal Asymptote: y = 0 (or y = d in transformed functions)\n\n➤ Growth vs. Decay:\n• b > 1: Exponential Growth (graph increases)\n• 0 < b < 1: Exponential Decay (graph decreases)\n• Output is always positive if a > 0\n\n➤ Graphing Steps:\n1. Choose x-values (e.g., -2 to 2)\n2. Calculate y-values\n3. Plot and sketch curve\n4. Label y-intercept and asymptote\n\nExamples:\nf(x) = 2^x + 1 → Growth, y-intercept = 2, asymptote = y = 1\nf(x) = 10(0.5)^x → Decay, y-intercept = 10, asymptote = y = 0\n\n➤ Transformations:\n• Vertical Stretch/Compression: a·b^x\n• Reflection over x-axis: −a·b^x\n• Vertical Shift: a·b^x + d → shifts graph up/down\n• Horizontal Shift: a·b^(x − h) → shifts graph right/left\n\n➤ Real-World Applications:\n• Population: P(t) = P₀(1 + r)^t\n• Decay: N(t) = N₀(1 − r)^t\n• Compound Interest: A = P(1 + r/n)^(nt)\n\n➤ Solving Exponential Equations:\n• If bases equal: a^x = a^y → x = y\n• If not: use logarithms (e.g., log₃(81) = 4)\n\n➤ Comparison:\nLinear: constant growth (y = 2x + 1)\nQuadratic: increasing non-linearly (y = x²)\nExponential: rapid multiplicative growth (y = 2^x)\n\n➤ Vocabulary:\nExponent, Base, Growth Rate, Decay Rate, Asymptote, Y-Intercept, Initial Value\n\n➤ Common Misconceptions:\n• Confusing growth and decay (check base)\n• Thinking the graph crosses the asymptote (it never does)\n• Ignoring order of operations when calculating powers',
		examples: [
			{
				passage:
					'Which of the following functions shows exponential decay?\nA. f(x) = 3^x\nB. f(x) = 0.8^x\nC. f(x) = −2^x\nD. f(x) = x^3',
				question: 'Identify the decay model from the options.',
				options: ['f(x) = 3^x', 'f(x) = 0.8^x', 'f(x) = −2^x', 'f(x) = x^3'],
				answer: 'f(x) = 0.8^x',
				explanation: 'Decay occurs when 0 < base < 1. Only f(x) = 0.8^x fits this.',
			},
			{
				passage: 'What is the y-intercept of f(x) = 4·2^x − 3?',
				question: 'Find f(0) for the function.',
				options: ['4', '−3', '1', '0'],
				answer: '1',
				explanation: 'f(0) = 4·2⁰ − 3 = 4 − 3 = 1',
			},
			{
				passage:
					'A population triples every year. If there are 2,000 people now, how many will there be in 3 years?',
				question: 'Use exponential growth formula P = 2000·3^t',
				options: ['6,000', '18,000', '54,000', '8,000'],
				answer: '54,000',
				explanation: '2000·3^3 = 2000·27 = 54,000',
			},
		],
		takeaways: [
			'Exponential functions model rapid growth or decay.',
			'The base b determines whether the function grows (b > 1) or decays (0 < b < 1).',
			'Graph features include y-intercept and horizontal asymptote.',
			'Real-world uses include population, interest, and radioactive decay.',
			'Use logarithms when solving exponential equations with different bases.',
			'Exponential functions eventually outpace linear and quadratic growth.',
			'Always evaluate powers before applying arithmetic when solving or graphing.',
		],
	},
	'dd6dd1cc-5043-4d92-b9be-2df4b03b46f0': {
		introduction:
			'This subtopic covers a broad range of nonlinear equations, including quadratic, radical, rational, and systems of equations. These types of equations appear frequently in the DSAT and real-world contexts like motion, work, and financial models. You’ll learn how to solve them using algebraic methods, interpret their graphs, and apply them in problem-solving situations.',
		details:
			'➤ Quadratic Equations:\n• Standard Form: ax² + bx + c = 0\n• Solving Methods:\n  – Factoring: Express as two binomials (e.g., (x + 2)(x + 3) = 0)\n  – Completing the Square: Convert to a perfect square\n  – Quadratic Formula: x = (-b ± √(b² - 4ac)) / 2a\n  – Graphing: Roots are the x-intercepts of the parabola\n• Graph Features:\n  – Opens up if a > 0, down if a < 0\n  – Vertex: (-b/2a, f(-b/2a))\n  – Axis of symmetry: x = -b/2a\n\n➤ Polynomial Equations:\n• Polynomial: Expression of the form anxⁿ + an-1xⁿ⁻¹ + ... + a₀\n• Degree = highest exponent, determines graph behavior\n• Graph Features:\n  – Turning points: max (n - 1)\n  – End behavior depends on degree and sign of leading term\n\n➤ Radical Equations and Functions:\n• A radical contains a square root, cube root, or higher-order root\n• f(x) = √x starts at (0,0); domain: x ≥ 0\n• Solve by isolating radical → square both sides → check for extraneous solutions\n• Common Simplifications:\n  – √50 = √(25 × 2) = 5√2\n  – √a × √b = √(ab)\n  – Rational exponents: √[n]{a^m} = a^(m/n)\n\n➤ Rational Equations and Functions:\n• A rational expression is a ratio of polynomials: f(x) = P(x)/Q(x), where Q(x) ≠ 0\n• Simplify by factoring numerator and denominator, canceling common terms\n• Solve by clearing denominators using LCD, solve resulting equation, check for invalid values\n• Graph Features:\n  – Vertical Asymptotes: Where denominator = 0\n  – Horizontal Asymptotes:\n     – Degree top < bottom: y = 0\n     – Degree top = bottom: ratio of leading coefficients\n     – Degree top > bottom: slant asymptote\n  – Holes: Occur where common factors cancel\n\n➤ Systems of Nonlinear Equations:\n• Involve equations where at least one is nonlinear (e.g., one quadratic and one linear)\n• Solve by:\n  – Substitution\n  – Elimination\n  – Graphing to find points of intersection\n\n➤ Real-World Applications:\n• Work problems: Work = Rate × Time\n  – If A works in x hours: Rate = 1/x\n  – Combined rate: add the individual rates\n• Motion problems: Distance = Rate × Time; solve for unknowns like speed or time\n• Mixture problems: Volume × Concentration = Total substance\n  – Set up equations and solve rationally\n\n➤ Transformations (also apply here):\n• f(x) + k → vertical shift\n• f(x − h) → horizontal shift\n• −f(x) → reflection over x-axis\n• af(x) → vertical stretch/compression\n\n➤ Common Mistakes to Avoid:\n• Cancelling terms across addition/subtraction — only allowed across multiplication\n• Forgetting to check for extraneous solutions in radical/rational equations\n• Using wrong LCD in rational equations\n• Misinterpreting asymptotes or domain issues in graphing',
		examples: [
			{
				passage: 'Solve the quadratic equation: x² + 6x + 5 = 0',
				question: 'Which method gives the solutions?',
				options: ['Quadratic formula', 'Graphing', 'Factoring', 'Guess and check'],
				answer: 'Factoring',
				explanation: '(x + 1)(x + 5) = 0 ⇒ x = -1, -5',
			},
			{
				passage: 'Solve for x: √(x + 3) = 5',
				question: 'What is the value of x?',
				options: ['x = 25', 'x = 22', 'x = 2', 'x = -22'],
				answer: 'x = 22',
				explanation: 'Square both sides: x + 3 = 25 → x = 22',
			},
			{
				passage: 'Simplify: √50',
				question: 'Which of the following is the correct simplified form?',
				options: ['5√2', '√25 + √2', '10', '25√2'],
				answer: '5√2',
				explanation: '√50 = √(25 × 2) = 5√2',
			},
			{
				passage: 'Solve: 1/x + 2/(x + 1) = (3x + 1)/(x(x + 1))',
				question: 'What is the correct first step?',
				options: [
					'Multiply each term by x + 1',
					'Combine numerators directly',
					'Multiply both sides by x(x + 1)',
					'Cross-multiply the fractions',
				],
				answer: 'Multiply both sides by x(x + 1)',
				explanation:
					'Clearing all denominators using the LCD simplifies solving rational equations.',
			},
			{
				passage:
					'A can do a job in 6 hours, B in 4. Working together, how long will it take?',
				question: 'Use work = rate × time to find total time.',
				options: ['5 hours', '2.4 hours', '10 hours', '3 hours'],
				answer: '2.4 hours',
				explanation: '1/6 + 1/4 = 5/12 ⇒ time = 12/5 = 2.4 hours',
			},
			{
				passage: 'Graph f(x) = 1 / (x - 3)',
				question: 'Where is the vertical asymptote?',
				options: ['x = 3', 'y = 0', 'x = 0', 'x = -3'],
				answer: 'x = 3',
				explanation: 'The denominator is 0 at x = 3, so that’s the vertical asymptote.',
			},
		],
		takeaways: [
			'Use factoring, completing the square, or the quadratic formula to solve quadratics.',
			'Radical and rational equations often produce extraneous solutions — always check them.',
			'Identify vertical/horizontal asymptotes when graphing rational functions.',
			'Isolate the radical and square both sides to solve radical equations.',
			'Use the LCD to eliminate fractions in rational equations.',
			'Real-world problems often use nonlinear models — set up equations carefully from the context.',
			'Always test for domain restrictions and undefined values.',
		],
	},
	'2072cca6-82e1-4e0f-9d14-25a9ff4465c1': {
		introduction:
			'Linear equations in one variable are the foundation of algebra. These equations involve a single variable raised to the first power and represent relationships that change at a constant rate. On the DSAT, you’ll be asked to solve such equations, understand when they have one, none, or infinite solutions, and apply them to real-world scenarios.',
		details:
			'➤ What Is a Linear Equation?\nA linear equation is any equation where the variable has an exponent of 1.\nGeneral form: ax + b = c\n– a, b, and c are constants\n– x is the variable\n\n➤ Solving Steps:\n1. Simplify both sides (distribute, combine like terms)\n2. Move variables to one side, constants to the other\n3. Isolate the variable\n4. Check your answer\n\nExample:\n3x - 7 = 11\n→ Add 7: 3x = 18\n→ Divide: x = 6\n\n➤ Types of Solutions:\n• One solution: Regular equation (e.g., x = 4)\n• No solution: Contradiction (e.g., 5 = 3)\n• Infinite solutions: Identity (e.g., x = x)\n\n➤ Applications:\n• Solving real-life word problems (cost, distance, age)\n• Interpreting the solution in context\n\n➤ Common Mistakes:\n• Incorrectly moving terms across =\n• Forgetting to perform inverse operations\n• Not checking for identity/contradiction cases',
		examples: [
			{
				passage: 'Solve for x: 2x + 3 = 11',
				question: 'What is the correct value of x?',
				options: ['x = 7', 'x = 4', 'x = 5', 'x = 3'],
				answer: 'x = 4',
				explanation: '2x + 3 = 11 → Subtract 3: 2x = 8 → Divide by 2: x = 4',
			},
			{
				passage: 'Determine the solution type: 5(x − 2) = 5x − 10',
				question: 'What kind of solution does this equation have?',
				options: ['No solution', 'One solution', 'Infinite solutions', 'Cannot determine'],
				answer: 'Infinite solutions',
				explanation:
					'Distribute: 5x − 10 = 5x − 10 → both sides identical → infinite solutions',
			},
			{
				passage: 'Does the equation 4x + 7 = 4x − 5 have any solutions?',
				question: 'Identify the type of solution.',
				options: [
					'One solution',
					'No solution',
					'Infinite solutions',
					'Zero is the solution',
				],
				answer: 'No solution',
				explanation: 'Subtract 4x: 7 = −5 → contradiction → no solution',
			},
		],
		takeaways: [
			'Linear equations in one variable take the form ax + b = c.',
			'Always isolate the variable step by step using inverse operations.',
			'Check if an equation results in one, none, or infinite solutions.',
			'Word problems often translate directly to linear equations.',
			'Verify your solutions by plugging back into the original equation.',
		],
	},
	'8c0c3f39-4bb1-44c1-b8cf-0850f4584508': {
		introduction:
			'Linear equations in two variables model relationships between two quantities. These equations graph as straight lines on the coordinate plane and are often written in the form y = mx + b, where m is the slope and b is the y-intercept. Understanding how to interpret, manipulate, and graph these equations is essential for solving DSAT problems involving lines, rates, and comparisons.',
		details:
			'➤ General Forms:\n• Slope-Intercept: y = mx + b\n• Standard: Ax + By = C\n• Point-Slope: y − y₁ = m(x − x₁)\n\n➤ Key Components:\n• Slope (m): Rate of change; rise/run\n• Y-intercept (b): Where the line crosses the y-axis\n• X-intercept: Where the line crosses the x-axis\n\n➤ Finding the Slope:\n• From graph: (change in y) / (change in x)\n• From two points: m = (y₂ − y₁)/(x₂ − x₁)\n\n➤ Converting Between Forms:\n• Solve for y to convert standard to slope-intercept\n• Use point-slope to write an equation given a point and slope\n\n➤ Graphing Steps:\n1. Identify slope and y-intercept\n2. Plot the y-intercept (0, b)\n3. Use the slope to find other points\n4. Draw a straight line through the points\n\n➤ Real-World Applications:\n• Modeling cost over time\n• Comparing rates (e.g., phone plans, jobs)\n• Tracking trends\n\n➤ Parallel and Perpendicular Lines:\n• Parallel: same slope, different intercepts\n• Perpendicular: slopes are negative reciprocals (m₁ · m₂ = −1)\n\n➤ Common Mistakes:\n• Mixing up slope and intercept\n• Forgetting to isolate y when converting to slope-intercept\n• Using incorrect rise/run direction',
		examples: [
			{
				passage: 'Find the slope of the line passing through (2, 3) and (6, 7)',
				question: 'What is the slope?',
				options: ['2', '1', '¼', '3'],
				answer: '1',
				explanation: 'm = (7 − 3)/(6 − 2) = 4/4 = 1',
			},
			{
				passage:
					'Which of the following is equivalent to 2x + 3y = 12 in slope-intercept form?',
				question: 'Convert the equation to y = mx + b form.',
				options: ['y = (−2/3)x + 4', 'y = (2/3)x + 4', 'y = (3/2)x + 6', 'y = −2x + 12'],
				answer: 'y = (−2/3)x + 4',
				explanation: 'Solve for y: 3y = −2x + 12 → y = (−2/3)x + 4',
			},
			{
				passage: 'The line y = 2x + 1 intersects the y-axis at which point?',
				question: 'Identify the y-intercept.',
				options: ['(2, 0)', '(0, 1)', '(1, 0)', '(0, 2)'],
				answer: '(0, 1)',
				explanation: 'The y-intercept is always (0, b); here b = 1.',
			},
		],
		takeaways: [
			'Linear equations in two variables represent straight lines on a graph.',
			'The slope tells you how steep the line is; the intercept tells you where it crosses the axis.',
			'Use slope-intercept form to quickly graph or interpret a line.',
			'Know how to convert between forms like standard and slope-intercept.',
			'Watch out for parallel and perpendicular relationships between lines.',
		],
	},
	'72dc3277-087d-4202-a9bc-5fa72c29f988': {
		introduction:
			"Linear functions describe relationships where the rate of change is constant. These are often used to model real-world situations involving pricing, motion, budgeting, and more. On the DSAT, you'll interpret linear functions presented in equations, tables, graphs, and word problems. Mastering this topic means understanding how to identify, create, and use these functions to solve problems.",
		details:
			'➤ Definition:\nA linear function has the form f(x) = mx + b, where:\n• m is the slope (rate of change)\n• b is the y-intercept (initial value)\n\n➤ Representations:\n• Equation: f(x) = 3x + 2\n• Table: x and f(x) values showing constant change\n• Graph: straight line\n• Verbal: Descriptions like “$5 per hour with a $10 base fee”\n\n➤ Identifying Linear Functions:\n• Constant first differences in a table\n• Straight-line graphs\n• Equations without exponents or variable products\n\n➤ Interpreting from Context:\n• Slope (m): the rate of change (e.g., cost per unit, speed)\n• Y-intercept (b): starting value (e.g., flat fee, starting amount)\n\n➤ Writing Linear Functions:\nFrom description:\n“$3 per book plus $5 shipping” → f(x) = 3x + 5\nFrom two points: Use slope formula, then point-slope or slope-intercept form\n\n➤ Applications:\n• Cost models\n• Distance vs. time\n• Earnings vs. hours\n• Conversions (e.g., °C to °F)\n\n➤ Common Mistakes:\n• Swapping slope and intercept roles\n• Assuming all functions are linear\n• Misreading context: e.g., thinking flat fee is slope',
		examples: [
			{
				passage:
					'A ride-sharing company charges $2 per mile and a $5 base fare. Which function represents this relationship?',
				question: 'Translate the pricing structure into a linear function.',
				options: ['f(x) = 5x + 2', 'f(x) = 2x + 5', 'f(x) = x/2 + 5', 'f(x) = 2x − 5'],
				answer: 'f(x) = 2x + 5',
				explanation: 'Slope = 2 (per mile), y-intercept = 5 (base fare).',
			},
			{
				passage:
					'A graph shows a line passing through points (0, 3) and (4, 11). What is the slope of the function?',
				question: 'Use slope = (y₂ − y₁)/(x₂ − x₁)',
				options: ['2', '1', '3', '4'],
				answer: '2',
				explanation: '(11 − 3)/(4 − 0) = 8/4 = 2',
			},
			{
				passage:
					'The table below shows a function:\n\nx: 1, 2, 3\nf(x): 4, 7, 10\n\nWhat is the equation of the function?',
				question: 'Write a linear equation matching the table values.',
				options: ['f(x) = 3x + 1', 'f(x) = 2x + 2', 'f(x) = x + 3', 'f(x) = 4x'],
				answer: 'f(x) = 3x + 1',
				explanation:
					'Each increase in x increases f(x) by 3. When x = 1, f(x) = 4 → f(x) = 3x + 1',
			},
		],
		takeaways: [
			'A linear function has a constant rate of change and graphs as a straight line.',
			'The slope represents the rate; the intercept represents the starting value.',
			'You can write linear functions from descriptions, graphs, or tables.',
			'Check for equal spacing and equal changes when analyzing tables.',
			'Linear functions are useful for modeling cost, speed, and other proportional relationships.',
		],
	},
	'e32ab858-4277-4ae8-b61e-0f3ceb4c057f': {
		introduction:
			'Linear inequalities describe a range of values rather than one specific solution. These inequalities are foundational in algebra and real-world decision-making—like budgets, speed limits, and working hours. In the DSAT, you’ll be asked to solve linear inequalities, graph their solutions on number lines or coordinate planes, and interpret them in context.',
		details:
			'➤ What Is a Linear Inequality?\nA linear inequality is similar to a linear equation but uses inequality symbols: <, ≤, >, ≥ instead of =.\nExamples:\n• x + 3 < 7\n• 2y − x ≥ 4\n\n➤ Solving Linear Inequalities:\n1. Use inverse operations to isolate the variable\n2. Flip the inequality sign when multiplying/dividing by a negative\n\nExample:\n−3x > 9 → divide by −3 → x < −3\n\n➤ Solution Sets:\n• Express as inequalities (x < 5)\n• Use interval notation ((−∞, 5))\n• Show on number lines (open or closed dots)\n\n➤ Graphing Two-Variable Inequalities:\n• Step 1: Rewrite in slope-intercept form\n• Step 2: Graph the boundary line\n– Use solid line for ≤ or ≥\n– Use dashed line for < or >\n• Step 3: Shade the correct side\n\n➤ Applications:\n• Budget constraints\n• Time limits\n• Performance minimums\n\n➤ Common Mistakes:\n• Forgetting to flip the inequality sign with negatives\n• Using solid lines for strict (<, >) inequalities\n• Misinterpreting the direction of shading',
		examples: [
			{
				passage: 'Solve the inequality: −2x + 5 > 1',
				question: 'What is the solution for x?',
				options: ['x < 2', 'x > 2', 'x < −2', 'x > −2'],
				answer: 'x < 2',
				explanation: '−2x > −4 → divide by −2 → x < 2',
			},
			{
				passage: 'Which graph represents the inequality y ≤ 2x − 1?',
				question: 'What type of line and shading does this inequality produce?',
				options: [
					'Solid line, shade below',
					'Solid line, shade above',
					'Dashed line, shade below',
					'Dashed line, shade above',
				],
				answer: 'Solid line, shade below',
				explanation: '“≤” means solid line and shading below the line',
			},
			{
				passage:
					'A student needs at least 75% on the final to pass. Which inequality shows this?',
				question: 'Translate the condition into a mathematical inequality.',
				options: ['x < 75', 'x ≥ 75', 'x = 75', 'x ≤ 75'],
				answer: 'x ≥ 75',
				explanation: '“At least” means the value can be 75 or more → x ≥ 75',
			},
		],
		takeaways: [
			'Solving inequalities is similar to solving equations, but watch out for negative multipliers.',
			'Use solid lines for ≤ or ≥ and dashed lines for < or > when graphing.',
			'Always flip the inequality sign when multiplying or dividing by a negative.',
			'Real-world problems often involve phrases like “at least,” “no more than,” or “greater than.”',
			'Graph the inequality by first drawing the boundary line, then shading appropriately.',
		],
	},
	'60833cfc-8d81-43a0-bd45-0f68358c2822': {
		introduction:
			'A system of linear equations consists of two or more equations with two variables. The goal is to find the values of the variables that satisfy all equations in the system simultaneously. These systems are used to model real-world problems involving comparisons, totals, or constraints. The DSAT may ask you to solve such systems algebraically or interpret their graphs.',
		details:
			'➤ Types of Systems:\n• Consistent and Independent: One solution (lines intersect)\n• Consistent and Dependent: Infinite solutions (same line)\n• Inconsistent: No solution (parallel lines)\n\n➤ Methods of Solving:\n1. **Substitution**:\n   – Solve one equation for one variable\n   – Substitute into the other equation\n\n2. **Elimination**:\n   – Align equations\n   – Add/subtract to eliminate a variable\n   – Solve the resulting equation, back-substitute\n\n3. **Graphing**:\n   – Graph both equations\n   – Identify the point of intersection\n\n➤ Interpreting Solutions:\n• A solution is a point (x, y) that satisfies both equations\n• No solution means the lines never intersect\n• Infinite solutions mean the two equations are equivalent\n\n➤ Real-World Applications:\n• Mixture problems\n• Total cost = fixed + variable\n• Distance = rate × time\n• Shared work or combined efforts\n\n➤ Common Mistakes:\n• Arithmetic errors during elimination or substitution\n• Forgetting to check for special cases (same line or parallel)\n• Incorrectly identifying slopes or intercepts from equations\n• Using wrong signs when aligning terms',
		examples: [
			{
				passage: 'Solve the system:\n\nx + y = 10\nx − y = 2',
				question: 'What is the solution (x, y)?',
				options: ['(6, 4)', '(5, 5)', '(4, 6)', '(3, 7)'],
				answer: '(6, 4)',
				explanation: 'Add the equations: 2x = 12 → x = 6 → plug into first: y = 4',
			},
			{
				passage:
					'What type of solution does this system have?\n\n2x − 3y = 6\n4x − 6y = 12',
				question: 'Classify the system.',
				options: ['One solution', 'No solution', 'Infinite solutions', 'Cannot determine'],
				answer: 'Infinite solutions',
				explanation:
					'Second equation is a multiple of the first → same line → infinite solutions',
			},
			{
				passage:
					'A concert sold 100 tickets for $800. Student tickets were $5 and adult tickets were $10. How many of each ticket were sold?',
				question: 'Which system represents this problem?',
				options: [
					'x + y = 100 and 5x + 10y = 800',
					'x + y = 800 and 10x + 5y = 100',
					'x + y = 100 and 10x + 5y = 800',
					'x + y = 100 and 5x + 5y = 800',
				],
				answer: 'x + y = 100 and 5x + 10y = 800',
				explanation: 'x = student, y = adult → total tickets = 100, total cost = 800',
			},
		],
		takeaways: [
			'Use substitution when one equation is already solved for a variable.',
			'Use elimination when variables are aligned and easy to cancel.',
			'Check whether the system has one, none, or infinite solutions.',
			'Real-world problems involving totals or comparisons can be modeled with systems.',
			'Graphing helps visualize the solution and type of system.',
		],
	},
	'137c29b5-87c1-4a58-819e-1178d0b0b09d': {
		introduction:
			'In this subtopic, you will learn how to calculate the perimeter, area, and volume of common geometric shapes. These measurements help describe the size, surface, and capacity of 2D and 3D objects. Whether you’re solving a DSAT geometry question or estimating materials for a project, mastering these formulas and concepts is essential.',
		details:
			'➤ Perimeter:\n• Measures the total boundary length of a 2D shape\n• Common Formulas:\n  – Square: P = 4s\n  – Rectangle: P = 2(l + w)\n  – Triangle: P = a + b + c\n  – Circle (Circumference): C = 2πr or C = πd\n  – Regular polygon: P = number of sides × side length\n\n➤ Area:\n• Measures the surface covered by a shape\n• Common Formulas:\n  – Square: A = s²\n  – Rectangle: A = l × w\n  – Triangle: A = ½ × b × h\n  – Parallelogram: A = b × h\n  – Trapezoid: A = ½ × h × (b₁ + b₂)\n  – Circle: A = πr²\n\n➤ Volume:\n• Measures space occupied by a 3D object\n• Common Formulas:\n  – Cube: V = s³\n  – Rectangular prism: V = l × w × h\n  – Cylinder: V = πr²h\n  – Sphere: V = (4/3)πr³\n  – Cone: V = (1/3)πr²h\n  – Rectangular pyramid: V = (1/3)lwh\n\n➤ Effects of Dimension Changes:\n• Perimeter changes linearly\n• Area changes with the square of the scale factor\n• Volume changes with the cube of the scale factor\n\n➤ Applications:\n• Fence → Perimeter\n• Paint needed → Area\n• Water in a tank → Volume\n• Package shipping → Volume and surface area\n\n➤ Common Mistakes:\n• Mixing up area and perimeter\n• Forgetting to square or cube the units\n• Using wrong units (e.g., cm³ for area)\n• Not converting mixed units (e.g., m and cm)',
		examples: [
			{
				passage: 'A rectangle has length 14 cm and width 6 cm.',
				question: 'What is its area?',
				options: ['20 cm²', '84 cm²', '42 cm²', '90 cm²'],
				answer: '84 cm²',
				explanation: 'Area = l × w = 14 × 6 = 84 cm²',
			},
			{
				passage: 'A square has a perimeter of 32 inches.',
				question: 'What is its area?',
				options: ['16 in²', '32 in²', '64 in²', '128 in²'],
				answer: '64 in²',
				explanation: 'Side = 32 ÷ 4 = 8 → Area = 8² = 64 in²',
			},
			{
				passage: 'The volume of a cube is 125 cm³.',
				question: 'What is the side length of the cube?',
				options: ['25 cm', '5 cm', '3 cm', '15 cm'],
				answer: '5 cm',
				explanation: 's³ = 125 → s = ³√125 = 5 cm',
			},
		],
		takeaways: [
			'Perimeter measures the boundary of a shape and uses linear units.',
			'Area measures surface coverage and uses square units.',
			'Volume measures 3D space and uses cubic units.',
			'Use appropriate formulas and be careful with unit conversions.',
			'When dimensions change, area and volume scale by the square or cube of the factor.',
		],
	},
	'1c26d576-69f7-48fd-9ef9-b8015484df9f': {
		introduction:
			'This subtopic covers properties of right triangles and foundational concepts in trigonometry. You’ll learn how to apply the Pythagorean Theorem and trigonometric ratios to find unknown sides or angles in right triangles. These tools are powerful for solving problems involving height, distance, and angles in real-world and geometric scenarios.',
		details:
			'➤ Right Triangles:\n• A right triangle has one 90° angle\n• The side opposite the right angle is the hypotenuse\n\n➤ Pythagorean Theorem:\n• a² + b² = c², where c is the hypotenuse\n• Use to find missing side lengths in right triangles\n\n➤ Special Right Triangles:\n• 45°-45°-90°: legs are equal; hypotenuse = leg × √2\n• 30°-60°-90°: short leg:x, long leg:x√3, hypotenuse:2x\n\n➤ Trigonometric Ratios:\nFor angle θ in a right triangle:\n• sin(θ) = opposite / hypotenuse\n• cos(θ) = adjacent / hypotenuse\n• tan(θ) = opposite / adjacent\n\n➤ Inverse Trigonometric Functions:\n• sin⁻¹, cos⁻¹, tan⁻¹ are used to find angles\n• Example: If sin(θ) = 0.5, then θ = sin⁻¹(0.5) = 30°\n\n➤ Applications:\n• Finding heights or distances using angles\n• Word problems involving ladders, ramps, buildings\n• Angle of elevation/depression problems\n\n➤ Calculator Use:\n• Make sure calculator is in degree mode when solving DSAT problems\n\n➤ Common Mistakes:\n• Mixing up opposite/adjacent sides\n• Forgetting to label sides relative to the angle\n• Using wrong mode (radians vs. degrees)\n• Incorrect rounding of trigonometric values',
		examples: [
			{
				passage:
					'In a right triangle, one leg is 6 units and the hypotenuse is 10 units. What is the length of the other leg?',
				question: 'Use the Pythagorean Theorem to solve.',
				options: ['4', '6', '8', '5'],
				answer: '8',
				explanation: '6² + b² = 10² → 36 + b² = 100 → b² = 64 → b = 8',
			},
			{
				passage:
					'In a 30°-60°-90° triangle, the shorter leg is 5. What is the length of the hypotenuse?',
				question: 'Use special triangle ratios.',
				options: ['5√3', '5', '10', '15'],
				answer: '10',
				explanation: 'In a 30°-60°-90°, hypotenuse = 2 × shorter leg = 2 × 5 = 10',
			},
			{
				passage:
					'A ladder leans against a wall forming a 60° angle with the ground. The ladder is 10 ft long. How high does it reach up the wall?',
				question: 'What is the height reached on the wall?',
				options: ['5 ft', '10 ft', '8.7 ft', '6 ft'],
				answer: '8.7 ft',
				explanation:
					'Use sin(60°) = opposite / 10 → opposite = 10 × sin(60°) ≈ 10 × 0.866 = 8.66',
			},
		],
		takeaways: [
			'Use the Pythagorean Theorem to find missing sides in right triangles.',
			'Recognize and apply special triangle ratios (45°-45°-90°, 30°-60°-90°).',
			'Use sine, cosine, and tangent to find unknown sides or angles.',
			'Label triangle sides correctly relative to the reference angle.',
			'Set calculator to degree mode and round properly when using trigonometric functions.',
		],
	},
	'4cb0aa64-c6de-4f76-a7cc-2b561387735b': {
		introduction:
			'Understanding lines, angles, and triangles is foundational to geometry. These concepts help us analyze shapes, calculate missing measures, and understand how figures relate to each other in space. This subtopic includes angle relationships, triangle classifications, triangle congruence, and triangle similarity — all of which are commonly tested on the DSAT.',
		details:
			'➤ Angle Relationships:\n• Adjacent angles: share a side\n• Vertical angles: opposite angles formed by intersecting lines; always equal\n• Complementary: add to 90°\n• Supplementary: add to 180°\n• Linear pair: adjacent + supplementary\n\n➤ Parallel Lines and a Transversal:\n• Corresponding angles: equal\n• Alternate interior/exterior angles: equal\n• Consecutive interior angles: supplementary\n\n➤ Triangle Properties:\n• Sum of interior angles = 180°\n• Exterior angle = sum of two remote interior angles\n• Triangle inequality: sum of any two sides > third side\n\n➤ Triangle Classification:\nBy sides:\n• Equilateral: 3 equal sides\n• Isosceles: 2 equal sides\n• Scalene: all sides different\n\nBy angles:\n• Acute: all angles < 90°\n• Right: one angle = 90°\n• Obtuse: one angle > 90°\n\n➤ Triangle Congruence (CPCTC):\n• SSS, SAS, ASA, AAS, HL\n• Proves two triangles are exactly equal in all dimensions\n\n➤ Triangle Similarity:\n• AA, SSS (proportional), SAS (angle between proportional sides)\n• Similar triangles have the same shape but different size\n• Corresponding angles equal, sides proportional\n\n➤ Applications:\n• Solving for missing sides or angles\n• Proving geometric relationships\n• Real-world scale problems\n\n➤ Common Mistakes:\n• Confusing congruent and similar\n• Using wrong side pairs in proportion\n• Not verifying triangle inequality\n• Misidentifying angle relationships',
		examples: [
			{
				passage:
					'Two angles form a linear pair and one measures 65°. What is the measure of the other?',
				question: 'Apply the concept of supplementary angles.',
				options: ['25°', '65°', '115°', '135°'],
				answer: '115°',
				explanation: 'Linear pair = supplementary → 180° − 65° = 115°',
			},
			{
				passage: 'In triangle ABC, angle A = 40°, angle B = 60°. What is angle C?',
				question: 'Use the triangle angle sum property.',
				options: ['60°', '80°', '100°', '90°'],
				answer: '80°',
				explanation: 'Sum = 180° → C = 180° − 40° − 60° = 80°',
			},
			{
				passage: 'Which triangle congruence condition proves two triangles are congruent?',
				question: 'Choose the valid condition.',
				options: ['SSA', 'SSS', 'AAA', 'ASS'],
				answer: 'SSS',
				explanation:
					'SSS (Side-Side-Side) is a valid congruence postulate. SSA and ASS are not.',
			},
			{
				passage: 'In triangle XYZ ~ triangle DEF, XY = 4, YZ = 6, DE = 6. What is EF?',
				question: 'Use proportional sides to find EF.',
				options: ['8', '9', '6', '10'],
				answer: '9',
				explanation: 'XY/DE = YZ/EF → 4/6 = 6/EF → EF = (6×6)/4 = 9',
			},
		],
		takeaways: [
			'Vertical, corresponding, and alternate interior angles are always equal.',
			'The sum of angles in any triangle is 180°.',
			'Use triangle congruence postulates (SSS, SAS, ASA, etc.) to prove triangles are identical.',
			'Similar triangles have equal angles and proportional sides.',
			'Pay attention to angle relationships in figures with parallel lines and transversals.',
		],
	},
	'3c1432e7-e701-4482-89f1-b270798cc2d4': {
		introduction:
			'Circles are unique geometric shapes where every point on the curve is equidistant from a central point. In this subtopic, you’ll explore key parts of a circle, angle and arc relationships, and how to calculate arc length and area of sectors — all of which are frequently tested on the DSAT.',
		details:
			'➤ Basic Definitions:\n• Radius: distance from center to edge\n• Diameter: longest chord (2 × radius)\n• Chord: a line segment connecting two points on the circle\n• Circumference: the perimeter of a circle (C = 2πr or C = πd)\n• Area: space inside the circle (A = πr²)\n\n➤ Arc and Angle Relationships:\n• Central angle: formed by two radii; equals arc it intercepts\n• Inscribed angle: vertex on the circle; half the intercepted arc\n• Arc: portion of the circle’s circumference\n  – Minor arc < 180°\n  – Major arc > 180°\n\n➤ Special Segments:\n• Tangent: touches the circle at one point; perpendicular to radius\n• Secant: line that intersects the circle at two points\n\n➤ Angle Rules:\n• Inscribed angle = ½ × central angle\n• Angles formed by chords: average of intercepted arcs\n• Angle formed by tangent and chord = ½ intercepted arc\n\n➤ Sector and Arc Calculations:\n• Arc Length = (θ/360) × 2πr\n• Sector Area = (θ/360) × πr²\n• θ is the central angle in degrees\n\n➤ Applications:\n• Ferris wheels, gears, pie slices\n• Finding distances and areas in circular motion problems\n\n➤ Common Mistakes:\n• Confusing central and inscribed angle rules\n• Forgetting to convert degrees to radians (only if needed)\n• Using full circle formulas on arc problems\n• Ignoring that tangent is perpendicular to radius at point of contact',
		examples: [
			{
				passage: 'A circle has a radius of 6 cm. What is its circumference?',
				question: 'Use the formula C = 2πr.',
				options: ['12π cm', '36π cm', '6π cm', '18π cm'],
				answer: '12π cm',
				explanation: 'C = 2π × 6 = 12π cm',
			},
			{
				passage:
					'In a circle, a central angle measures 60°. What fraction of the circle’s area does the sector represent?',
				question: 'Use sector area proportion.',
				options: ['1/6', '1/4', '1/3', '1/2'],
				answer: '1/6',
				explanation: 'Sector = (60/360) = 1/6 of full circle',
			},
			{
				passage:
					'An inscribed angle intercepts an arc of 80°. What is the measure of the angle?',
				question: 'Apply the inscribed angle rule.',
				options: ['40°', '80°', '100°', '160°'],
				answer: '40°',
				explanation: 'Inscribed angle = ½ × 80° = 40°',
			},
		],
		takeaways: [
			'The circumference and area of a circle are based on the radius and π.',
			'Inscribed angles are always half the measure of their intercepted arcs.',
			'Tangents are perpendicular to the radius at the point of contact.',
			'Use proportions when calculating arc length and sector area.',
			'Identify whether an angle is central or inscribed before applying a formula.',
		],
	},
	'1abfbf1d-b013-468f-a3bc-3193db8d8cc7': {
		introduction:
			'Percentages are used to represent parts of a whole and are commonly found in discounts, tax, interest rates, and population data. On the DSAT, percentage problems test your ability to calculate percentage change, compare values, and apply percentage-based reasoning in real-world scenarios.',
		details:
			'➤ Basic Concepts:\n• Percent means “per hundred”: 35% = 35/100 = 0.35\n• To convert a decimal to a percent, multiply by 100\n• To convert a percent to a decimal, divide by 100\n\n➤ Percentage of a Quantity:\n• Formula: Percentage × Total = Part\n  – Example: 20% of 60 = 0.2 × 60 = 12\n\n➤ Percentage Increase/Decrease:\n• % Change = (Difference / Original) × 100\n• Increase: Final > Original\n• Decrease: Final < Original\n\n➤ Reverse Percent Problems:\n• Given the final value and percent change, find the original:\n  – Example: A price increased by 20% to become $120. What was the original?\n  – Solution: 1.20x = 120 → x = 100\n\n➤ Multi-Step Percent Problems:\n• Often appear in context: tax + discount, profit margin + cost\n• Apply each step sequentially (not simultaneously)\n\n➤ Real-World Applications:\n• Retail (discounts, markup, profit)\n• Banking (interest, loan, savings)\n• Data comparisons (survey growth, population change)\n\n➤ Common Mistakes:\n• Using new value as base instead of original\n• Not distinguishing between increase and decrease\n• Ignoring whether percent applies to part or whole',
		examples: [
			{
				passage:
					'An item originally cost $80 and is now priced at $60. What is the percent decrease?',
				question: 'Use the percentage decrease formula.',
				options: ['20%', '25%', '33.3%', '15%'],
				answer: '25%',
				explanation: 'Change = 80 − 60 = 20 → 20/80 × 100 = 25%',
			},
			{
				passage:
					'After a 15% increase, a population became 115,000. What was the original population?',
				question: 'Solve using reverse percentage.',
				options: ['100,000', '110,000', '99,000', '105,000'],
				answer: '100,000',
				explanation: 'Let x be the original → x × 1.15 = 115,000 → x = 100,000',
			},
			{
				passage:
					'A $50 item is discounted by 20% and then taxed 10%. What is the final price?',
				question: 'Apply multi-step percentage operations.',
				options: ['$45', '$48.40', '$44', '$40'],
				answer: '$44',
				explanation: 'Discount: 50 − 10 = 40 → Tax: 40 × 1.1 = $44',
			},
		],
		takeaways: [
			'Always calculate percent change based on the original amount.',
			'Break multi-step percentage problems into clear stages.',
			'Reverse percentage problems require dividing by the growth/shrink factor.',
			'Know when to use percent as a multiplier vs. a comparison ratio.',
			'Percent problems appear in nearly every real-world data context on the DSAT.',
		],
	},
	'38338fc6-f179-49f6-bafd-05e32fe4a9ff': {
		introduction:
			'Probability measures how likely an event is to occur and is a foundational concept in data analysis. This subtopic includes both basic probability and conditional probability, which considers the likelihood of an event given that another has occurred. On the DSAT, you’ll solve problems involving theoretical, experimental, and conditional probability.',
		details:
			'➤ Basic Probability:\n• Probability = (Number of favorable outcomes) / (Total outcomes)\n• Values range from 0 (impossible) to 1 (certain)\n• Example: Probability of rolling a 4 on a die = 1/6\n\n➤ Compound Events:\n• “AND” = multiply probabilities (if independent)\n• “OR” = add probabilities (if mutually exclusive)\n• Example: P(A or B) = P(A) + P(B) − P(A and B) when not exclusive\n\n➤ Experimental vs. Theoretical:\n• Theoretical: based on known outcomes (like dice)\n• Experimental: based on observed data\n• Larger sample sizes → more accurate experimental results\n\n➤ Conditional Probability:\n• Probability of event A given event B: P(A | B) = P(A and B) / P(B)\n• Applies when events are dependent\n• Example: Drawing two cards without replacement changes the probabilities\n\n➤ Two-Way Tables:\n• Useful for calculating conditional probability\n• Identify totals and intersections clearly\n• Check whether values are joint (both events) or marginal (totals)\n\n➤ Common Mistakes:\n• Confusing independent vs. dependent events\n• Forgetting to subtract overlap in “OR” probability\n• Using incorrect denominator in conditional probability',
		examples: [
			{
				passage:
					'A bag contains 4 red, 3 blue, and 3 green marbles. What is the probability of drawing a blue marble?',
				question: 'Use basic probability.',
				options: ['1/3', '3/10', '3/11', '1/4'],
				answer: '3/10',
				explanation: 'Total = 10 marbles → blue = 3 → P = 3/10',
			},
			{
				passage:
					'The probability of A is 0.4, B is 0.3, and both A and B is 0.1. What is the probability of A or B?',
				question: 'Use P(A or B) = P(A) + P(B) − P(A and B)',
				options: ['0.6', '0.7', '0.9', '0.8'],
				answer: '0.6',
				explanation: '0.4 + 0.3 − 0.1 = 0.6',
			},
			{
				passage:
					'In a class, 18 students play soccer, 10 play tennis, and 6 play both. What is the probability that a randomly selected student plays tennis given they play soccer?',
				question: 'Use conditional probability: P(Tennis | Soccer)',
				options: ['6/18', '6/10', '10/18', '22/28'],
				answer: '6/18',
				explanation: 'P(T | S) = P(S ∩ T) / P(S) = 6 / 18 = 1/3',
			},
		],
		takeaways: [
			'Probability = favorable / total outcomes.',
			'Use addition for “or” and multiplication for “and” — only if events are independent.',
			'Conditional probability requires focusing on the correct subset.',
			'Visual aids like tables or trees can help organize probabilities.',
			'Be cautious about whether events are mutually exclusive or overlapping.',
		],
	},
	'c76aee50-69de-4205-bf11-29d05d5a2a36': {
		introduction:
			'This subtopic focuses on understanding and applying ratios, rates, proportional reasoning, and unit conversions. On the DSAT, you’ll frequently encounter problems that test your ability to compare quantities, recognize proportional relationships, and work with different units of measure in real-world scenarios.',
		details:
			'➤ Ratios:\n• A ratio compares two quantities: a to b, a:b, or a/b\n• Can represent part-to-part, part-to-whole, or whole-to-whole relationships\n\n➤ Rates:\n• A rate compares two quantities with different units (e.g., miles per hour)\n• Unit rate: denominator is 1 (e.g., 60 miles in 2 hours → 30 mph)\n\n➤ Proportions:\n• Two equal ratios: a/b = c/d\n• Cross multiplication: ad = bc\n• Used to solve for unknowns in equivalent relationships\n\n➤ Identifying Proportionality:\n• Table: constant ratio (y ÷ x)\n• Graph: straight line through origin\n• Equation: y = kx (k is the constant of proportionality)\n\n➤ Percent Proportions:\n• Can represent percent problems: part/whole = percent/100\n\n➤ Scaling and Conversions:\n• Use ratios to scale shapes, recipes, or maps\n• Unit conversions: multiply by conversion factors (e.g., 1 inch = 2.54 cm)\n\n➤ Common Mistakes:\n• Mixing units in rate calculations\n• Assuming all relationships are proportional\n• Not simplifying ratios or checking for constant rate',
		examples: [
			{
				passage:
					'A recipe uses 2 cups of flour for every 3 cups of sugar. How much flour is needed for 9 cups of sugar?',
				question: 'Use a proportion to find the missing value.',
				options: ['3', '5', '6', '7'],
				answer: '6',
				explanation: '2/3 = x/9 → cross-multiply: 3x = 18 → x = 6',
			},
			{
				passage: 'A car travels 180 miles in 3 hours. What is its unit rate?',
				question: 'Find the speed in miles per hour.',
				options: ['60 mph', '90 mph', '30 mph', '45 mph'],
				answer: '60 mph',
				explanation: '180 ÷ 3 = 60 mph',
			},
			{
				passage: 'Which equation shows a proportional relationship?',
				question: 'Identify the correct form.',
				options: ['y = 3x + 2', 'y = x²', 'y = 5x', 'y = x − 1'],
				answer: 'y = 5x',
				explanation: 'y = 5x is in the form y = kx, indicating proportionality.',
			},
		],
		takeaways: [
			'A ratio is a comparison; a rate is a ratio with different units.',
			'Proportions help solve for unknowns when two ratios are equal.',
			'Proportional relationships graph as straight lines through the origin.',
			'Unit rates and conversions are key to real-world DSAT problems.',
			'Always check for consistent units and simplify when necessary.',
		],
	},
	'8beafbe1-11d5-4a01-990c-8028cbe15259': {
		introduction:
			'This subtopic focuses on how data is distributed and how we summarize it using measures like mean, median, mode, range, and standard deviation. Understanding the center and spread of one-variable data is key for interpreting graphs, identifying trends, and comparing data sets in real-world and DSAT contexts.',
		details:
			'Measures of Center:\n' +
			'- Mean: arithmetic average (sum of values ÷ number of values)\n' +
			'- Median: middle value when data is ordered\n' +
			'- Mode: most frequent value (can be more than one or none)\n\n' +
			'Measures of Spread:\n' +
			'- Range: maximum − minimum\n' +
			'- Interquartile Range (IQR): Q3 − Q1 (middle 50% of data)\n' +
			'- Standard Deviation: measures how far data points typically deviate from the mean\n' +
			'  - Low SD: data clustered around mean\n' +
			'  - High SD: data widely spread\n\n' +
			'Shape of Distribution:\n' +
			'- Symmetric: mean ≈ median\n' +
			'- Skewed right: mean > median\n' +
			'- Skewed left: mean < median\n\n' +
			'Outliers:\n' +
			'- Affect mean significantly, not median\n' +
			'- Can influence shape and spread\n\n' +
			'Interpreting Box Plots and Histograms:\n' +
			'- Box plots: visualize median, quartiles, and outliers\n' +
			'- Histograms: show frequency in intervals\n\n' +
			'Common Mistakes:\n' +
			'- Confusing mean and median when data is skewed\n' +
			'- Assuming mode always exists or is unique\n' +
			'- Misreading scales on box plots or histograms',
		examples: [
			{
				passage: 'Data: 5, 7, 8, 10, 10, 12, 15. What is the median?',
				question: 'Find the middle value.',
				options: ['8', '10', '9', '7'],
				answer: '10',
				explanation: 'Median = middle of ordered data = 10',
			},
			{
				passage: 'Which statement is true for a skewed right distribution?',
				question: 'Compare mean and median.',
				options: ['Mean < Median', 'Mean = Median', 'Mean > Median', 'Mean is always 0'],
				answer: 'Mean > Median',
				explanation: 'Skewed right → longer tail to the right → mean is pulled higher',
			},
			{
				passage:
					'A data set has a mean of 60 and a standard deviation of 0. What does this mean?',
				question: 'Interpret the standard deviation.',
				options: [
					'All data values are different',
					'Data is spread out from the mean',
					'All values are equal to 60',
					'Standard deviation cannot be zero',
				],
				answer: 'All values are equal to 60',
				explanation: 'SD = 0 means no variability → every value = mean = 60',
			},
		],
		takeaways: [
			'Use mean, median, and mode to understand center — but choose based on context.',
			'Range and IQR help describe how spread out data is.',
			'Standard deviation measures how consistently values cluster around the mean.',
			'Shape (skewed or symmetric) affects which center measure is most representative.',
			'Be cautious of outliers — they distort the mean but not the median.',
		],
	},
	'4991ebd2-e546-4f87-bd5d-12e08be63199': {
		introduction:
			'This subtopic covers how to represent and analyze relationships between two variables using scatterplots, trend lines, and correlation. On the DSAT, you may be asked to interpret data from graphs, identify trends, or determine which models best describe the relationship.',
		details:
			'Scatterplots:\n' +
			'- Show pairs of numerical data plotted as points on a graph\n' +
			'- Each point represents one observation (x, y)\n' +
			'- Useful for identifying patterns and trends\n\n' +
			'Types of Correlation:\n' +
			'- Positive: as x increases, y increases\n' +
			'- Negative: as x increases, y decreases\n' +
			'- No correlation: no visible trend in data\n\n' +
			'Line of Best Fit (Trend Line):\n' +
			'- A straight line that closely follows the data trend\n' +
			'- Can be used to make predictions (extrapolation or interpolation)\n' +
			'- Slope indicates rate of change\n\n' +
			'Correlation vs. Causation:\n' +
			'- Correlation does not imply causation\n' +
			'- Example: Ice cream sales and drowning deaths may correlate due to season, not cause\n\n' +
			'Residuals:\n' +
			'- Residual = actual y − predicted y\n' +
			'- A good model has residuals that are small and randomly distributed\n\n' +
			'Common Mistakes:\n' +
			'- Confusing weak and no correlation\n' +
			'- Misinterpreting direction of correlation\n' +
			'- Assuming causation based on trend\n' +
			'- Using trend lines to predict far beyond the data range',
		examples: [
			{
				passage: 'A scatterplot shows a downward trend. What does this imply?',
				question: 'Identify the correlation.',
				options: [
					'Positive correlation',
					'No correlation',
					'Negative correlation',
					'Causation',
				],
				answer: 'Negative correlation',
				explanation:
					'As one variable increases, the other decreases — this is a negative correlation.',
			},
			{
				passage: 'Which scenario likely shows a positive correlation?',
				question: 'Choose the example with a meaningful positive trend.',
				options: [
					'Height and shoe size',
					'Temperature and coat sales',
					'Gas mileage and car weight',
					'Number of absences and test scores',
				],
				answer: 'Height and shoe size',
				explanation:
					'Taller people tend to have larger shoe sizes — a positive relationship.',
			},
			{
				passage:
					'A line of best fit is drawn through a scatterplot. One point lies far above the line. What is this called?',
				question: 'Use terminology from residual analysis.',
				options: ['Median', 'Residual', 'Slope', 'Intercept'],
				answer: 'Residual',
				explanation: 'The vertical distance from the point to the line is the residual.',
			},
		],
		takeaways: [
			'Scatterplots visually represent relationships between two variables.',
			'Positive and negative correlations indicate direction; no trend = no correlation.',
			'Lines of best fit can be used to model and predict data behavior.',
			'Correlation does not prove causation — look for outside factors.',
			'Residuals help assess how well a model fits the data.',
		],
	},
	'17c404fb-8f4d-4cc4-8610-7805c113f1c3': {
		introduction:
			'This subtopic teaches you how to evaluate statistical claims by understanding the structure of studies. The DSAT often includes passages or scenarios that ask whether data supports a conclusion and whether the method of data collection (observational vs. experimental) is valid or biased.',
		details:
			'Types of Studies:\n' +
			'- Observational Study: researchers observe but do not interfere or assign treatments\n' +
			'- Experiment: researchers assign treatments or conditions to study cause and effect\n\n' +
			'Key Terms:\n' +
			'- Randomization: subjects are randomly assigned to groups to reduce bias\n' +
			'- Control Group: receives no treatment or a standard treatment for comparison\n' +
			'- Placebo: inactive treatment used to test effectiveness\n' +
			'- Blind Study: participants don’t know which group they’re in\n' +
			'- Double-Blind: neither participants nor experimenters know group assignments\n\n' +
			'Common Biases:\n' +
			'- Sampling bias: sample doesn’t represent population\n' +
			'- Response bias: participants give inaccurate answers\n' +
			'- Selection bias: certain groups are excluded\n\n' +
			'Valid Conclusions:\n' +
			'- Observational studies can show correlation but not causation\n' +
			'- Only well-designed experiments can establish causation\n\n' +
			'Common Mistakes:\n' +
			'- Assuming correlation proves cause\n' +
			'- Ignoring sample size or control groups\n' +
			'- Failing to distinguish between observation and intervention',
		examples: [
			{
				passage:
					'Researchers observe that students who sleep more tend to perform better in school. What type of study is this?',
				question: 'Identify the method used.',
				options: [
					'Controlled experiment',
					'Survey study',
					'Observational study',
					'Double-blind experiment',
				],
				answer: 'Observational study',
				explanation: 'No treatment was assigned — only observations were made.',
			},
			{
				passage: 'Which feature increases the reliability of an experiment?',
				question: 'Identify best experimental practice.',
				options: [
					'Using a small sample',
					'Choosing only volunteers',
					'Random assignment to groups',
					'Letting participants pick groups',
				],
				answer: 'Random assignment to groups',
				explanation: 'Randomization reduces bias and improves reliability.',
			},
		],
		takeaways: [
			'Observational studies identify patterns but can’t prove cause and effect.',
			'Experiments use controlled conditions to test causal relationships.',
			'Randomization, control groups, and blinding increase experimental validity.',
			'Be cautious of biases in sample selection and data interpretation.',
			'Distinguish between observational and experimental designs when evaluating claims.',
		],
	},
	'7f3f2ff5-d901-4c5c-a9d2-4a0596789bd6': {
		introduction:
			'This subtopic focuses on how we use data from a sample to draw conclusions about a larger population. On the DSAT, you’ll evaluate survey data, understand margin of error, and determine when an inference is justified based on sample size and sampling method.',
		details:
			'Sampling and Inference:\n' +
			'- A sample is a subset of a population\n' +
			'- If representative and random, a sample can be used to infer characteristics about the population\n\n' +
			'Margin of Error:\n' +
			'- Describes the range likely to contain the true population value\n' +
			'- Example: 52% ± 3% means the actual percent is likely between 49% and 55%\n' +
			'- Larger samples → smaller margin of error\n\n' +
			'Confidence Intervals:\n' +
			'- Range of values based on sample data that likely contains the true value\n' +
			'- 95% confidence level is common: means if the study were repeated many times, the interval would contain the true value 95% of the time\n\n' +
			'Valid Inference Requires:\n' +
			'- Random, representative sample\n' +
			'- Clear population target\n' +
			'- Sufficient sample size\n\n' +
			'Invalid Inference:\n' +
			'- Sample is biased (e.g., only one location or demographic)\n' +
			'- Sample size is too small to generalize\n\n' +
			'Common Mistakes:\n' +
			'- Ignoring margin of error when comparing results\n' +
			'- Assuming sample results exactly match population\n' +
			'- Making inferences about subgroups not well represented in the sample',
		examples: [
			{
				passage:
					'A poll finds 60% of voters support a candidate with a margin of error of ±4%. What is the range of possible support?',
				question: 'Apply the margin of error.',
				options: ['56% to 64%', '50% to 70%', '60% to 64%', '58% to 62%'],
				answer: '56% to 64%',
				explanation: '60% ± 4% → Range: 56% to 64%',
			},
			{
				passage: 'Which of the following makes a conclusion most valid?',
				question: 'Determine best data collection method.',
				options: [
					'Surveying friends only',
					'Choosing a random sample from the population',
					'Using a small sample with strong opinions',
					'Polling only one neighborhood',
				],
				answer: 'Choosing a random sample from the population',
				explanation: 'Random samples reduce bias and improve generalizability.',
			},
		],
		takeaways: [
			'Use sample statistics to estimate population values — with caution.',
			'Always interpret results in the context of margin of error.',
			'Random, representative samples yield stronger, more valid inferences.',
			'Confidence intervals give a range of plausible values, not exact ones.',
			'Don’t overgeneralize from biased or small samples.',
		],
	},
};
